import React, { useState, useRef, useEffect } from "react";
import { useSelector } from "react-redux";
import {
  Typography,
  Box,
  Button,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Card,
  CardContent,
  Avatar,
  Chip,
  IconButton,
  List,
  ListItem,
  Divider,
  LinearProgress,
  Paper,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Backdrop,
  CircularProgress,
  Alert,
  Snackbar,
  useTheme,
  Fade,
  ThemeProvider,
  createTheme,
  alpha,
  Stack,
  Badge,
} from "@mui/material";
import Grid from "@mui/material/Unstable_Grid2";
import {
  Image as ImageIcon,
  Chat as ChatIcon,
  Person as PersonIcon,
  PhotoCamera,
  Brush,
  EmojiEmotions,
  ViewComfy,
  AutoAwesome,
  Send,
  ContentCopy,
  MoreVert,
  Edit,
  Delete,
  PushPin,
  Share,
  History,
  Fullscreen,
  FullscreenExit,
  Warning,
  Clear,
  Build,
  Bolt,
  Psychology,
  Palette,
  RocketLaunch,
  AddComment,
  ExpandMore,
  Circle,
  TuneRounded,
  FormatPaint,
  Download,
  SaveAlt,
  Close as CloseIcon,
  GridView,
  ViewCarousel,
  DownloadDone,
  Info,
  ArrowBack,
  ArrowForward,
  ZoomIn,
  ZoomOut,
  RestartAlt,
  Favorite,
  FavoriteBorder,
  Collections,
  Wallpaper,
  Schedule,
} from "@mui/icons-material";
import Close from "@mui/icons-material/Close";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  aiSmartDesk,
  aiHumanizer,
  generateAIImage,
} from "../../src/services/aiIntelligenzServices";

import { useCheckFeatureAccess } from "../components/CheckFeature";
import { useCheckSubscriptionPlanAccess } from "../components/SubscriptionAccess";
import SubscriptionRequired from "../components/SubscriptionRequired";
import { checkAIRequestLimit } from "../components/aiRequestLimit";
import { addUserLogs } from "../../src/services/userLogServices";

const MAX_CHARACTERS = 1500;
const MAX_CHAT_WORDS = 1500;
const MAX_CHAT_WORDS_PREMIUM = 3500;
const MAX_HISTORY_IMAGES = 5; // Store last 5 images

// ─── Theme ──────────────────────────────────────────────────────────────────
const aiIntelligenzLightTheme = createTheme({
  palette: {
    mode: "light",
    primary: { main: "#4F6EF7", light: "#7B93FF", dark: "#2D4FD6" },
    secondary: { main: "#A855F7", light: "#C084FC", dark: "#7E22CE" },
    success: { main: "#10B981", light: "#34D399", dark: "#059669" },
    warning: { main: "#F59E0B" },
    error: { main: "#EF4444" },
    text: { primary: "#0F172A", secondary: "#64748B" },
    background: { default: "#F1F5FF", paper: "#FFFFFF" },
    divider: "rgba(15,23,42,0.08)",
  },
  typography: {
    fontFamily: '"DM Sans", "Segoe UI", sans-serif',
    h4: { fontWeight: 800, letterSpacing: "-0.02em" },
    h5: { fontWeight: 700, letterSpacing: "-0.015em" },
    h6: { fontWeight: 700, letterSpacing: "-0.01em" },
    subtitle1: { fontWeight: 600 },
    subtitle2: {
      fontWeight: 600,
      fontSize: "0.8rem",
      letterSpacing: "0.04em",
      textTransform: "uppercase",
    },
    body2: { lineHeight: 1.7 },
    caption: { color: "#64748B", lineHeight: 1.5 },
  },
  shape: { borderRadius: 16 },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: "none",
          fontWeight: 700,
          borderRadius: 12,
          letterSpacing: "-0.01em",
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          backgroundImage: "none",
          borderRadius: 20,
          border: "1px solid rgba(15,23,42,0.06)",
          boxShadow:
            "0 1px 3px rgba(15,23,42,0.06), 0 8px 24px rgba(15,23,42,0.04)",
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          "& .MuiOutlinedInput-root": {
            borderRadius: 12,
            "&:hover .MuiOutlinedInput-notchedOutline": {
              borderColor: "#4F6EF7",
            },
          },
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: { borderRadius: 8, fontWeight: 600, fontSize: "0.75rem" },
      },
    },
    MuiToggleButton: {
      styleOverrides: {
        root: {
          borderRadius: "12px !important",
          border: "1.5px solid rgba(15,23,42,0.1) !important",
          textTransform: "none",
          fontWeight: 600,
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        rounded: { borderRadius: 16 },
      },
    },
  },
});

// ─── Fullscreen Image Gallery Component ───────────────────────────────────
const FullscreenImageGallery = ({
  open,
  onClose,
  images,
  currentImageIndex,
  onIndexChange,
  onDownload,
  onCopy,
  onDelete,
}) => {
  const [zoomLevel, setZoomLevel] = useState(1);
  const [showInfo, setShowInfo] = useState(true);
  const [viewMode, setViewMode] = useState("carousel");
  const [selectedImageIndex, setSelectedImageIndex] =
    useState(currentImageIndex);
  const [imageError, setImageError] = useState(false);

  useEffect(() => {
    setSelectedImageIndex(currentImageIndex);
    setImageError(false);
  }, [currentImageIndex]);

  const handleZoomIn = () => {
    setZoomLevel((prev) => Math.min(prev + 0.25, 3));
  };

  const handleZoomOut = () => {
    setZoomLevel((prev) => Math.max(prev - 0.25, 0.5));
  };

  const handleResetZoom = () => {
    setZoomLevel(1);
  };

  const handleNextImage = () => {
    const newIndex = (selectedImageIndex + 1) % images.length;
    setSelectedImageIndex(newIndex);
    setImageError(false);
    setZoomLevel(1);
    onIndexChange?.(newIndex);
  };

  const handlePrevImage = () => {
    const newIndex = (selectedImageIndex - 1 + images.length) % images.length;
    setSelectedImageIndex(newIndex);
    setImageError(false);
    setZoomLevel(1);
    onIndexChange?.(newIndex);
  };

  const handleKeyDown = (e) => {
    if (e.key === "ArrowRight") handleNextImage();
    if (e.key === "ArrowLeft") handlePrevImage();
    if (e.key === "Escape") onClose();
    if (e.key === "+" || e.key === "=") handleZoomIn();
    if (e.key === "-") handleZoomOut();
    if (e.key === "0") handleResetZoom();
    if (e.key === "i" || e.key === "I") setShowInfo((prev) => !prev);
  };

  useEffect(() => {
    if (open) {
      window.addEventListener("keydown", handleKeyDown);
      return () => window.removeEventListener("keydown", handleKeyDown);
    }
  }, [open, selectedImageIndex]);

  const currentImage = images[selectedImageIndex];

  const handleImageError = () => {
    setImageError(true);
  };

  return (
    <Dialog
      fullScreen
      open={open}
      onClose={onClose}
      TransitionComponent={Fade}
      PaperProps={{
        sx: {
          backgroundColor: "#0f172a",
          backgroundImage:
            "radial-gradient(circle at 20% 50%, rgba(139, 92, 246, 0.1) 0%, transparent 50%)",
        },
      }}
    >
      <Box sx={{ height: "100vh", display: "flex", flexDirection: "column" }}>
        {/* Header */}
        <Paper
          elevation={0}
          sx={{
            p: 2,
            background: "linear-gradient(135deg, #8B5CF6 0%, #EC4899 100%)",
            color: "white",
            position: "relative",
            zIndex: 10,
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
              <IconButton
                onClick={onClose}
                sx={{
                  color: "white",
                  backgroundColor: "rgba(255, 255, 255, 0.15)",
                  backdropFilter: "blur(10px)",
                  "&:hover": { backgroundColor: "rgba(255, 255, 255, 0.25)" },
                }}
              >
                <FullscreenExit />
              </IconButton>

              <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                <Box
                  sx={{
                    width: 44,
                    height: 44,
                    borderRadius: "12px",
                    background: "rgba(255, 255, 255, 0.2)",
                    backdropFilter: "blur(10px)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    border: "1px solid rgba(255, 255, 255, 0.3)",
                  }}
                >
                  <Collections sx={{ fontSize: 24, color: "white" }} />
                </Box>
                <Box>
                  <Typography variant="h6" fontWeight="bold">
                    AI Image Gallery - Fullscreen View
                  </Typography>
                  <Typography variant="caption" sx={{ opacity: 0.9 }}>
                    {images.length} {images.length === 1 ? "image" : "images"} •{" "}
                    {selectedImageIndex + 1}/{images.length}
                  </Typography>
                </Box>
              </Box>
            </Box>

            <Box sx={{ display: "flex", gap: 1 }}>
              <ToggleButtonGroup
                value={viewMode}
                exclusive
                onChange={(e, val) => val && setViewMode(val)}
                size="small"
                sx={{
                  backgroundColor: "rgba(255, 255, 255, 0.15)",
                  backdropFilter: "blur(10px)",
                  "& .MuiToggleButton-root": {
                    color: "white",
                    borderColor: "rgba(255, 255, 255, 0.3)",
                    "&.Mui-selected": {
                      backgroundColor: "rgba(255, 255, 255, 0.25)",
                      color: "white",
                    },
                  },
                }}
              >
                <ToggleButton value="grid">
                  <GridView fontSize="small" />
                </ToggleButton>
                <ToggleButton value="carousel">
                  <ViewCarousel fontSize="small" />
                </ToggleButton>
              </ToggleButtonGroup>

              <IconButton
                onClick={() => setShowInfo(!showInfo)}
                sx={{
                  color: "white",
                  backgroundColor: "rgba(255, 255, 255, 0.15)",
                  backdropFilter: "blur(10px)",
                  "&:hover": { backgroundColor: "rgba(255, 255, 255, 0.25)" },
                }}
              >
                <Info />
              </IconButton>
            </Box>
          </Box>
        </Paper>

        {/* Main Content */}
        <Box sx={{ flex: 1, display: "flex", overflow: "hidden" }}>
          {/* Image Display Area */}
          <Box
            sx={{
              flex: showInfo ? 3 : 4,
              position: "relative",
              backgroundColor: "#0a0a0a",
              overflow: "auto",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              p: 2,
            }}
          >
            {viewMode === "carousel" ? (
              <Box sx={{ position: "relative", width: "100%", height: "100%" }}>
                {/* Navigation Arrows */}
                <IconButton
                  onClick={handlePrevImage}
                  sx={{
                    position: "absolute",
                    left: 20,
                    top: "50%",
                    transform: "translateY(-50%)",
                    backgroundColor: "rgba(0, 0, 0, 0.5)",
                    color: "white",
                    zIndex: 2,
                    "&:hover": { backgroundColor: "rgba(0, 0, 0, 0.7)" },
                  }}
                >
                  <ArrowBack />
                </IconButton>

                <IconButton
                  onClick={handleNextImage}
                  sx={{
                    position: "absolute",
                    right: 20,
                    top: "50%",
                    transform: "translateY(-50%)",
                    backgroundColor: "rgba(0, 0, 0, 0.5)",
                    color: "white",
                    zIndex: 2,
                    "&:hover": { backgroundColor: "rgba(0, 0, 0, 0.7)" },
                  }}
                >
                  <ArrowForward />
                </IconButton>

                {/* Current Image */}
                <Box
                  sx={{
                    height: "100%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    transform: `scale(${zoomLevel})`,
                    transition: "transform 0.2s",
                  }}
                >
                  {currentImage ? (
                    imageError ? (
                      <Box
                        sx={{
                          textAlign: "center",
                          p: 4,
                          backgroundColor: "rgba(255,255,255,0.1)",
                          borderRadius: 2,
                        }}
                      >
                        <ImageIcon
                          sx={{
                            fontSize: 64,
                            color: "rgba(255,255,255,0.3)",
                            mb: 2,
                          }}
                        />
                        <Typography variant="h6" color="white">
                          Failed to load image
                        </Typography>
                        <Typography
                          variant="body2"
                          color="rgba(255,255,255,0.7)"
                        >
                          The image could not be loaded. Please try again.
                        </Typography>
                      </Box>
                    ) : (
                      <img
                        src={currentImage.url}
                        alt={`Generated ${selectedImageIndex + 1}`}
                        style={{
                          maxWidth: "100%",
                          maxHeight: "100%",
                          objectFit: "contain",
                          borderRadius: 8,
                          boxShadow: "0 20px 40px rgba(0, 0, 0, 0.4)",
                        }}
                        onError={handleImageError}
                      />
                    )
                  ) : (
                    <Typography color="white">No image selected</Typography>
                  )}
                </Box>
              </Box>
            ) : (
              <Box sx={{ height: "100%", overflow: "auto", width: "100%" }}>
                <Grid container spacing={2} sx={{ p: 2 }}>
                  {images.map((image, index) => (
                    <Grid xs={12} sm={6} md={4} lg={3} key={image.id}>
                      <Paper
                        elevation={4}
                        sx={{
                          position: "relative",
                          overflow: "hidden",
                          borderRadius: 2,
                          cursor: "pointer",
                          transition: "all 0.2s",
                          border:
                            selectedImageIndex === index
                              ? "3px solid #8B5CF6"
                              : "none",
                          "&:hover": {
                            transform: "scale(1.02)",
                            "& .grid-actions": { opacity: 1 },
                          },
                        }}
                        onClick={() => {
                          setSelectedImageIndex(index);
                          setImageError(false);
                          onIndexChange?.(index);
                        }}
                      >
                        <Box sx={{ position: "relative", paddingTop: "75%" }}>
                          <img
                            src={image.url}
                            alt={`Generated ${index + 1}`}
                            style={{
                              position: "absolute",
                              top: 0,
                              left: 0,
                              width: "100%",
                              height: "100%",
                              objectFit: "cover",
                            }}
                            onError={(e) => {
                              e.target.src =
                                "https://via.placeholder.com/400x300?text=Image+Load+Error";
                            }}
                          />
                        </Box>

                        <Box
                          className="grid-actions"
                          sx={{
                            position: "absolute",
                            top: 8,
                            right: 8,
                            display: "flex",
                            gap: 0.5,
                            opacity: 0,
                            transition: "opacity 0.2s",
                          }}
                        >
                          <IconButton
                            size="small"
                            sx={{ bgcolor: "rgba(0,0,0,0.6)", color: "white" }}
                            onClick={(e) => {
                              e.stopPropagation();
                              onDownload(image.url, index);
                            }}
                          >
                            <Download fontSize="small" />
                          </IconButton>
                          <IconButton
                            size="small"
                            sx={{ bgcolor: "rgba(0,0,0,0.6)", color: "white" }}
                            onClick={(e) => {
                              e.stopPropagation();
                              onCopy(image.url);
                            }}
                          >
                            <ContentCopy fontSize="small" />
                          </IconButton>
                        </Box>

                        <Chip
                          label={image.style}
                          size="small"
                          sx={{
                            position: "absolute",
                            bottom: 8,
                            left: 8,
                            bgcolor: "rgba(139, 92, 246, 0.9)",
                            color: "white",
                            textTransform: "capitalize",
                          }}
                        />
                      </Paper>
                    </Grid>
                  ))}
                </Grid>
              </Box>
            )}

            {/* Zoom Controls */}
            {viewMode === "carousel" && currentImage && !imageError && (
              <Paper
                elevation={3}
                sx={{
                  position: "absolute",
                  bottom: 20,
                  right: 20,
                  display: "flex",
                  gap: 0.5,
                  p: 0.5,
                  backgroundColor: "rgba(0, 0, 0, 0.6)",
                  backdropFilter: "blur(10px)",
                  borderRadius: 2,
                  zIndex: 3,
                }}
              >
                <IconButton
                  size="small"
                  onClick={handleZoomOut}
                  sx={{ color: "white" }}
                >
                  <ZoomOut />
                </IconButton>
                <Typography
                  variant="caption"
                  sx={{
                    color: "white",
                    display: "flex",
                    alignItems: "center",
                    px: 1,
                  }}
                >
                  {Math.round(zoomLevel * 100)}%
                </Typography>
                <IconButton
                  size="small"
                  onClick={handleZoomIn}
                  sx={{ color: "white" }}
                >
                  <ZoomIn />
                </IconButton>
                <IconButton
                  size="small"
                  onClick={handleResetZoom}
                  sx={{ color: "white" }}
                >
                  <RestartAlt />
                </IconButton>
              </Paper>
            )}
          </Box>

          {/* Info Panel */}
          {showInfo && currentImage && (
            <Paper
              elevation={0}
              sx={{
                width: 320,
                backgroundColor: "background.paper",
                borderLeft: "1px solid",
                borderColor: "divider",
                overflow: "auto",
              }}
            >
              <Box sx={{ p: 3 }}>
                <Typography
                  variant="h6"
                  gutterBottom
                  sx={{ display: "flex", alignItems: "center", gap: 1 }}
                >
                  <Info color="primary" />
                  Image Details
                </Typography>

                <Divider sx={{ my: 2 }} />

                <Box sx={{ mb: 3 }}>
                  <Typography
                    variant="subtitle2"
                    color="text.secondary"
                    gutterBottom
                  >
                    Prompt
                  </Typography>
                  <Paper
                    variant="outlined"
                    sx={{
                      p: 2,
                      bgcolor: "rgba(139, 92, 246, 0.03)",
                      borderRadius: 2,
                      maxHeight: 150,
                      overflow: "auto",
                      borderColor: "rgba(139, 92, 246, 0.2)",
                    }}
                  >
                    <Typography variant="body2">
                      {currentImage.prompt}
                    </Typography>
                  </Paper>
                </Box>

                <Grid container spacing={2} sx={{ mb: 3 }}>
                  <Grid xs={6}>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      display="block"
                    >
                      Style
                    </Typography>
                    <Chip
                      label={currentImage.style}
                      size="small"
                      sx={{
                        mt: 0.5,
                        bgcolor: "#8B5CF6",
                        color: "white",
                        textTransform: "capitalize",
                      }}
                    />
                  </Grid>
                  <Grid xs={6}>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      display="block"
                    >
                      Source
                    </Typography>
                    <Chip
                      label={
                        currentImage.isHistory
                          ? "From History"
                          : "Newly Generated"
                      }
                      size="small"
                      sx={{
                        mt: 0.5,
                        bgcolor: currentImage.isHistory ? "#F59E0B" : "#10B981",
                        color: "white",
                      }}
                    />
                  </Grid>
                  <Grid xs={12}>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      display="block"
                    >
                      Generated
                    </Typography>
                    <Typography variant="body2" sx={{ fontSize: "0.8rem" }}>
                      {new Date(currentImage.timestamp).toLocaleString()}
                    </Typography>
                  </Grid>
                </Grid>

                <Typography
                  variant="subtitle2"
                  color="text.secondary"
                  gutterBottom
                >
                  Actions
                </Typography>
                <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                  <Button
                    fullWidth
                    variant="contained"
                    startIcon={<Download />}
                    onClick={() =>
                      onDownload(currentImage.url, selectedImageIndex)
                    }
                    sx={{
                      bgcolor: "#8B5CF6",
                      "&:hover": { bgcolor: "#7C3AED" },
                    }}
                  >
                    Download
                  </Button>
                  <Button
                    fullWidth
                    variant="outlined"
                    startIcon={<ContentCopy />}
                    onClick={() => onCopy(currentImage.url)}
                    sx={{
                      borderColor: "#8B5CF6",
                      color: "#8B5CF6",
                      "&:hover": {
                        borderColor: "#7C3AED",
                        bgcolor: "rgba(139, 92, 246, 0.05)",
                      },
                    }}
                  >
                    Copy to Clipboard
                  </Button>
                </Box>
              </Box>
            </Paper>
          )}
        </Box>

        {/* Footer */}
        <Paper
          elevation={0}
          sx={{
            p: 2,
            backgroundColor: "background.paper",
            borderTop: "1px solid",
            borderColor: "divider",
          }}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Box sx={{ display: "flex", gap: 2 }}>
              <Typography variant="caption" color="text.secondary">
                Use arrow keys to navigate • +/- to zoom • ESC to exit • I to
                toggle info
              </Typography>
            </Box>
            <Box sx={{ display: "flex", gap: 1 }}>
              <Button
                variant="outlined"
                size="small"
                startIcon={<CloseIcon />}
                onClick={onClose}
              >
                Close Gallery
              </Button>
            </Box>
          </Box>
        </Paper>
      </Box>
    </Dialog>
  );
};

// ─── Screen Info Overlay ─────────────────────────────────────────────────────
const ScreenInfoOverlay = ({ activeTab }) => {
  const cardRef = useRef(null);
  const [position, setPosition] = useState({ x: 100, y: 100 });
  const [dragging, setDragging] = useState(false);
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const [isVisible, setIsVisible] = useState(true);

  const info = {
    generator: {
      title: "AI GenArt Studio",
      description: "Transform ideas into stunning visuals",
      icon: "🎨",
      color: "#8B5CF6",
    },
    chatbot: {
      title: "AI SmartDesk",
      description: "Intelligent conversations & task assistance",
      icon: "💭",
      color: "#A855F7",
    },
    humanizer: {
      title: "Humanize ToneCraft",
      description: "Refine AI text to sound more natural",
      icon: "✍️",
      color: "#10B981",
    },
  }[activeTab] || {
    title: "AI Smart Engine Hub",
    description: "Your command center for AI tools",
    icon: "🚀",
    color: "#4F6EF7",
  };

  const handleMouseDown = (e) => {
    setDragging(true);
    const rect = cardRef.current.getBoundingClientRect();
    setOffset({ x: e.clientX - rect.left, y: e.clientY - rect.top });
    document.body.style.userSelect = "none";
  };
  const handleMouseMove = (e) => {
    if (!dragging) return;
    setPosition({ x: e.clientX - offset.x, y: e.clientY - offset.y });
  };
  const handleMouseUp = () => {
    setDragging(false);
    document.body.style.userSelect = "auto";
  };
  useEffect(() => {
    window.addEventListener("mousemove", handleMouseMove);
    window.addEventListener("mouseup", handleMouseUp);
    return () => {
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("mouseup", handleMouseUp);
    };
  });

  if (!isVisible) return null;

  return (
    <Box
      ref={cardRef}
      sx={{
        position: "fixed",
        right: position.x,
        bottom: position.y,
        zIndex: 2000,
        background: "rgba(255,255,255,0.85)",
        backdropFilter: "blur(16px)",
        borderRadius: 3,
        p: 2,
        width: 230,
        boxShadow:
          "0 8px 32px rgba(15,23,42,0.12), 0 0 0 1px rgba(15,23,42,0.06)",
        cursor: dragging ? "grabbing" : "default",
        border: `1.5px solid ${info.color}22`,
      }}
    >
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 1,
        }}
      >
        <Box
          onMouseDown={handleMouseDown}
          sx={{ display: "flex", alignItems: "center", gap: 1, cursor: "grab" }}
        >
          <Box
            sx={{
              width: 32,
              height: 32,
              borderRadius: 2,
              background: `${info.color}15`,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: "1.1rem",
            }}
          >
            {info.icon}
          </Box>
          <Typography
            variant="body2"
            sx={{ color: info.color, fontWeight: 700, fontSize: "0.8rem" }}
          >
            {info.title}
          </Typography>
        </Box>
        <IconButton
          size="small"
          onClick={() => setIsVisible(false)}
          sx={{
            color: "#ff0000",
            "&:hover": { color: "#EF4444", background: "rgba(239,68,68,0.08)" },
          }}
        >
          <Close sx={{ fontSize: 14 }} />
        </IconButton>
      </Box>
      <Typography
        variant="caption"
        sx={{ color: "#4a4a4a", display: "block", mb: 1.5, lineHeight: 1.4 }}
      >
        {info.description}
      </Typography>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1,
          pt: 1,
          borderTop: "1px solid rgba(15,23,42,0.06)",
        }}
      >
        <Box
          sx={{
            width: 6,
            height: 6,
            borderRadius: "50%",
            background: info.color,
            animation: "ping 1.5s ease infinite",
            "@keyframes ping": {
              "0%,100%": { opacity: 1, transform: "scale(1)" },
              "50%": { opacity: 0.4, transform: "scale(0.6)" },
            },
          }}
        />
        <Typography
          variant="caption"
          sx={{ color: "#94A3B8", fontWeight: 600 }}
        >
          Currently active
        </Typography>
      </Box>
    </Box>
  );
};

const AIIntelligenceBackground = ({ children }) => {
  return (
    <Box
      sx={{
        position: "relative",
        height: "calc(100vh - 131px)",
        minHeight: "calc(100vh - 131px)",
        overflowY: "auto",
        overflowX: "hidden",
        overscrollBehavior: "contain",
        background:
          "linear-gradient(135deg,#EEF2FF 0%,#F5F3FF 40%,#EFF6FF 70%,#F0FDF9 100%)",
        isolation: "isolate",
        scrollbarWidth: "none",
        msOverflowStyle: "none",
        "&::-webkit-scrollbar": {
          width: 0,
          height: 0,
          display: "none",
        },
      }}
    >
      {/* Mesh blobs */}
      {[
        {
          top: "-10%",
          left: "-5%",
          bg: "radial-gradient(circle,rgba(79,110,247,0.12) 0%,transparent 70%)",
          size: "50vw",
        },
        {
          top: "30%",
          right: "-10%",
          bg: "radial-gradient(circle,rgba(168,85,247,0.10) 0%,transparent 70%)",
          size: "40vw",
        },
        {
          bottom: "-5%",
          left: "20%",
          bg: "radial-gradient(circle,rgba(16,185,129,0.08) 0%,transparent 70%)",
          size: "35vw",
        },
      ].map((b, i) => (
        <Box
          key={i}
          sx={{
            position: "absolute",
            width: b.size,
            height: b.size,
            top: b.top,
            left: b.left,
            right: b.right,
            bottom: b.bottom,
            background: b.bg,
            zIndex: 0,
            pointerEvents: "none",
          }}
        />
      ))}

      {/* Floating particles */}
      {[...Array(18)].map((_, i) => (
        <Box
          key={i}
          sx={{
            position: "absolute",
            width: i % 4 === 0 ? 8 : 4,
            height: i % 4 === 0 ? 8 : 4,
            borderRadius: "50%",
            background:
              i % 3 === 0
                ? "rgba(79,110,247,0.25)"
                : i % 3 === 1
                  ? "rgba(168,85,247,0.2)"
                  : "rgba(16,185,129,0.2)",
            left: `${5 + Math.random() * 90}%`,
            top: `${5 + Math.random() * 90}%`,
            zIndex: 0,
            pointerEvents: "none",
            animation: `drift ${10 + Math.random() * 12}s ease-in-out infinite`,
            animationDelay: `${Math.random() * 6}s`,
            "@keyframes drift": {
              "0%,100%": { transform: "translate(0,0)" },
              "33%": {
                transform: `translate(${20 - Math.random() * 40}px,${-20 - Math.random() * 30}px)`,
              },
              "66%": {
                transform: `translate(${-10 + Math.random() * 30}px,${10 + Math.random() * 20}px)`,
              },
            },
          }}
        />
      ))}

      {/* Floating emoji icons */}
      {["🧠", "🤖", "✨", "💬", "🖼️"].map((icon, i) => (
        <Box
          key={icon}
          sx={{
            position: "absolute",
            fontSize: "1.8rem",
            opacity: 0.07,
            pointerEvents: "none",
            zIndex: 0,
            left: `${[5, 85, 88, 8, 14][i]}%`,
            top: `${[10, 12, 60, 62, 40][i]}%`,
            animation: `floatIcon ${7 + i}s ease-in-out infinite`,
            animationDelay: `${i * 0.7}s`,
            "@keyframes floatIcon": {
              "0%,100%": { transform: "translateY(0) rotate(0deg)" },
              "50%": { transform: "translateY(-18px) rotate(6deg)" },
            },
          }}
        >
          {icon}
        </Box>
      ))}

      <Box sx={{ position: "relative", zIndex: 1 }}>{children}</Box>
    </Box>
  );
};

// ─── Humanizer Tab ───────────────────────────────────────────────────────────
const HumanizerTab = ({
  humanizerLevel,
  setHumanizerLevel,
  userID,
  accessToLight,
  accessToBalanced,
  accessToMaximum,
  userPlan,
}) => {
  const [humanizeText, setHumanizeText] = useState("");
  const [humanizedResult, setHumanizedResult] = useState("");
  const [isHumanizing, setIsHumanizing] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [fullscreenDialogOpen, setFullscreenDialogOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const textareaRef = useRef(null);

  const getMaxWords = () =>
    userPlan === "premium" ? MAX_CHAT_WORDS_PREMIUM : MAX_CHAT_WORDS;

  const isLevelAccessible = (level) => {
    switch (level) {
      case "light":
        return accessToLight;
      case "balanced":
        return accessToBalanced;
      case "aggressive":
        return accessToMaximum;
      default:
        return true;
    }
  };

  const handleHumanizeText = async () => {
    if (!humanizeText.trim()) {
      toast.warning("Please enter text to humanize", {
        position: "top-center",
        autoClose: 3000,
      });
      return;
    }
    const wordCount = humanizeText.trim().split(/\s+/).length;
    if (wordCount > getMaxWords()) {
      toast.error(`Text exceeds ${getMaxWords()} word limit`, {
        position: "top-center",
        autoClose: 3000,
      });
      return;
    }
    if (!isLevelAccessible(humanizerLevel)) {
      toast.error(`"${humanizerLevel}" level is not available in your plan`);
      return;
    }

    setIsHumanizing(true);
    setLoading(true);
    setHumanizedResult("");
    try {
      const response = await aiHumanizer(humanizeText, humanizerLevel);
      let humanizedText =
        response.data?.data?.humanized_text ||
        response.data?.humanized_text ||
        (typeof response.data === "string" ? response.data : null) ||
        response?.humanized_text ||
        "Received response but couldn't parse the humanized text.";
      setHumanizedResult(humanizedText);
      await addUserLogs({
        userId: sessionStorage.getItem("userId"),
        organizationId: sessionStorage.getItem("organizationId"),
        actionType: "humanizer",
      });
      toast.success("Text humanized successfully!", {
        position: "top-center",
        autoClose: 3000,
      });
    } catch (error) {
      const errorMsg =
        error.response?.data?.message || error.message || "Unknown error";
      setHumanizedResult("Sorry, something went wrong. Error: " + errorMsg);
      toast.error(`Failed to humanize text: ${errorMsg}`, {
        position: "top-center",
        autoClose: 5000,
      });
    } finally {
      setIsHumanizing(false);
      setLoading(false);
    }
  };

  const handleCopyResult = () => {
    if (!humanizedResult) return;
    navigator.clipboard
      .writeText(humanizedResult)
      .then(() =>
        toast.success("Copied to clipboard!", {
          position: "top-center",
          autoClose: 2000,
        }),
      )
      .catch(() =>
        toast.error("Failed to copy", {
          position: "top-center",
          autoClose: 3000,
        }),
      );
  };

  const handleTextChange = (e) => {
    const newText = e.target.value;
    const wordCount = newText.trim().split(/\s+/).length;
    if (wordCount > getMaxWords()) {
      const trimmedText = newText
        .trim()
        .split(/\s+/)
        .slice(0, getMaxWords())
        .join(" ");
      setHumanizeText(trimmedText);
      toast.warning(`Text trimmed to ${getMaxWords()} words`, {
        position: "top-center",
        autoClose: 3000,
      });
    } else {
      setHumanizeText(newText);
    }
  };

  const handlePaste = async (e) => {
    e.preventDefault();
    try {
      const pastedText = await navigator.clipboard.readText();
      const currentWords = humanizeText.trim().split(/\s+/).length;
      const remainingWords = getMaxWords() - currentWords;
      if (remainingWords <= 0) {
        toast.warning(
          `Cannot paste - text already at ${getMaxWords()} word limit`,
          { position: "top-center", autoClose: 3000 },
        );
        return;
      }
      const pastedWords = pastedText.trim().split(/\s+/);
      const wordsToAdd = pastedWords.slice(0, remainingWords).join(" ");
      setHumanizeText(humanizeText + (humanizeText ? " " : "") + wordsToAdd);
      if (pastedWords.length > remainingWords)
        toast.warning(
          `Pasted text trimmed - only ${remainingWords} words available`,
          { position: "top-center", autoClose: 3000 },
        );
    } catch {
      const pastedText = e.clipboardData.getData("text");
      const remainingWords =
        getMaxWords() - humanizeText.trim().split(/\s+/).length;
      const wordsToAdd = pastedText
        .trim()
        .split(/\s+/)
        .slice(0, remainingWords)
        .join(" ");
      setHumanizeText(humanizeText + (humanizeText ? " " : "") + wordsToAdd);
    }
  };

  const wordCount = humanizeText.trim()
    ? humanizeText.trim().split(/\s+/).length
    : 0;
  const isTextValid =
    humanizeText.trim().length > 0 && wordCount <= getMaxWords();
  const wordPct = Math.min((wordCount / getMaxWords()) * 100, 100);

  const levels = [
    {
      value: "light",
      label: "Light",
      desc: "Minimal changes",
      color: "#4F6EF7",
      glow: "rgba(79,110,247,0.25)",
      accessible: accessToLight,
    },
    {
      value: "balanced",
      label: "Balanced",
      desc: "Optimal balance",
      color: "#10B981",
      glow: "rgba(16,185,129,0.25)",
      accessible: accessToBalanced,
    },
    {
      value: "aggressive",
      label: "Maximum",
      desc: "Max humanization",
      color: "#A855F7",
      glow: "rgba(168,85,247,0.25)",
      accessible: accessToMaximum,
    },
  ];

  return (
    <Grid container spacing={3}>
      {/* Input card */}
      <Grid xs={12} md={7}>
        <Card sx={{ height: "100%" }}>
          <CardContent sx={{ p: 3 }}>
            {/* Header */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                mb: 3,
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                <Box
                  sx={{
                    width: 40,
                    height: 40,
                    borderRadius: 2,
                    background: "linear-gradient(135deg,#10B981,#059669)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <FormatPaint sx={{ color: "white", fontSize: 20 }} />
                </Box>
                <Box>
                  <Typography variant="h6">AI Humanizer</Typography>
                  <Typography variant="caption">
                    Transform AI text into natural human writing
                  </Typography>
                </Box>
              </Box>
              {(humanizeText || humanizedResult) && (
                <Button
                  size="small"
                  startIcon={<Clear sx={{ fontSize: 14 }} />}
                  onClick={() => {
                    setHumanizeText("");
                    setHumanizedResult("");
                  }}
                  sx={{
                    color: "#EF4444",
                    border: "1px solid rgba(15,23,42,0.1)",
                    borderRadius: 2,
                    px: 1.5,
                    py: 0.5,
                    fontSize: "0.75rem",
                    "&:hover": {
                      borderColor: "#EF4444",
                      color: "#EF4444",
                      background: "rgba(239,68,68,0.04)",
                    },
                  }}
                >
                  Clear All
                </Button>
              )}
            </Box>

            {/* Level selector */}
            <Typography variant="subtitle2" sx={{ mb: 1.5, color: "#64748B" }}>
              Humanization Level
            </Typography>
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr 1fr",
                gap: 1.5,
                mb: 3,
              }}
            >
              {levels.map((lv) => (
                <Box
                  key={lv.value}
                  onClick={() => {
                    if (lv.accessible) setHumanizerLevel(lv.value);
                    else
                      toast.error(
                        `"${lv.value}" level is not available in your plan`,
                      );
                  }}
                  sx={{
                    p: 1.5,
                    borderRadius: 2,
                    textAlign: "center",
                    cursor: lv.accessible ? "pointer" : "not-allowed",
                    border: `2px solid ${humanizerLevel === lv.value ? lv.color : "rgba(15,23,42,0.08)"}`,
                    background:
                      humanizerLevel === lv.value
                        ? `${lv.color}0D`
                        : "transparent",
                    boxShadow:
                      humanizerLevel === lv.value
                        ? `0 0 16px ${lv.glow}`
                        : "none",
                    transition: "all 0.2s",
                    opacity: lv.accessible ? 1 : 0.45,
                    "&:hover": lv.accessible
                      ? {
                          borderColor: lv.color,
                          background: `${lv.color}08`,
                          transform: "translateY(-1px)",
                        }
                      : {},
                  }}
                >
                  <Typography
                    variant="body2"
                    sx={{
                      fontWeight: 700,
                      color: humanizerLevel === lv.value ? lv.color : "#0F172A",
                    }}
                  >
                    {lv.label}
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{
                      color: humanizerLevel === lv.value ? lv.color : "#94A3B8",
                    }}
                  >
                    {lv.accessible ? lv.desc : "Premium"}
                  </Typography>
                </Box>
              ))}
            </Box>

            <Typography variant="subtitle2" sx={{ mb: 1, color: "#64748B" }}>
              AI-Generated Text to Humanize
            </Typography>
            <TextField
              fullWidth
              multiline
              rows={8}
              placeholder="Paste your AI-generated text here..."
              variant="outlined"
              value={humanizeText}
              onChange={handleTextChange}
              onPaste={handlePaste}
              inputRef={textareaRef}
              error={wordCount > getMaxWords()}
              sx={{
                mb: 1.5,
                "& .MuiOutlinedInput-root": {
                  background: "rgba(241,245,255,0.6)",
                },
              }}
            />

            {/* Word meter */}
            <Box
              sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 2 }}
            >
              <Box sx={{ flex: 1 }}>
                <LinearProgress
                  variant="determinate"
                  value={wordPct}
                  sx={{
                    height: 5,
                    borderRadius: 5,
                    background: "rgba(15,23,42,0.07)",
                    "& .MuiLinearProgress-bar": {
                      borderRadius: 5,
                      background:
                        wordCount > getMaxWords()
                          ? "#EF4444"
                          : wordPct > 80
                            ? "#F59E0B"
                            : "linear-gradient(90deg,#4F6EF7,#10B981)",
                    },
                  }}
                />
              </Box>
              <Typography
                variant="caption"
                sx={{
                  whiteSpace: "nowrap",
                  color: wordCount > getMaxWords() ? "#EF4444" : "#64748B",
                  fontWeight: 600,
                }}
              >
                {wordCount} / {getMaxWords()} words
              </Typography>
              {wordCount > getMaxWords() && (
                <Warning sx={{ fontSize: 16, color: "#EF4444" }} />
              )}
            </Box>

            <Button
              fullWidth
              variant="contained"
              size="large"
              startIcon={loading ? null : <AutoAwesome />}
              onClick={handleHumanizeText}
              disabled={loading || !isTextValid}
              sx={{
                py: 1.6,
                borderRadius: 2,
                fontWeight: 800,
                fontSize: "0.95rem",
                background: "linear-gradient(135deg,#10B981,#059669)",
                boxShadow: "0 4px 16px rgba(16,185,129,0.35)",
                "&:hover": {
                  boxShadow: "0 6px 24px rgba(16,185,129,0.45)",
                  transform: "translateY(-1px)",
                },
                "&:disabled": {
                  background: "rgba(15,23,42,0.08)",
                  boxShadow: "none",
                },
              }}
            >
              {loading ? (
                <>
                  <CircularProgress size={18} sx={{ color: "white", mr: 1 }} />
                  Humanizing...
                </>
              ) : (
                "Humanize Text"
              )}
            </Button>
          </CardContent>
        </Card>
      </Grid>

      {/* Result card */}
      <Grid xs={12} md={5}>
        <Card sx={{ height: "100%" }}>
          <CardContent
            sx={{
              p: 3,
              display: "flex",
              flexDirection: "column",
              height: "100%",
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                mb: 2,
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                <Box
                  sx={{
                    width: 40,
                    height: 40,
                    borderRadius: 2,
                    background:
                      "linear-gradient(135deg,rgba(16,185,129,0.15),rgba(5,150,105,0.1))",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <AutoAwesome sx={{ color: "#10B981", fontSize: 20 }} />
                </Box>
                <Typography variant="h6">Humanized Result</Typography>
              </Box>
              {humanizedResult && (
                <IconButton
                  size="small"
                  onClick={() => setFullscreenDialogOpen(true)}
                  sx={{
                    background: "rgba(79,110,247,0.08)",
                    color: "#4F6EF7",
                    borderRadius: 1.5,
                    width: 32,
                    height: 32,
                    "&:hover": { background: "rgba(79,110,247,0.15)" },
                  }}
                >
                  <Fullscreen sx={{ fontSize: 18 }} />
                </IconButton>
              )}
            </Box>

            {loading ? (
              <Box
                sx={{
                  flex: 1,
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  py: 4,
                  borderRadius: 3,
                  background:
                    "linear-gradient(135deg,rgba(16,185,129,0.04),rgba(5,150,105,0.02))",
                  border: "1.5px dashed rgba(16,185,129,0.2)",
                }}
              >
                <Box sx={{ position: "relative", mb: 2 }}>
                  <CircularProgress size={48} sx={{ color: "#10B981" }} />
                  <Box
                    sx={{
                      position: "absolute",
                      inset: 0,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <AutoAwesome sx={{ fontSize: 18, color: "#10B981" }} />
                  </Box>
                </Box>
                <Typography
                  variant="body2"
                  sx={{ color: "#10B981", fontWeight: 600 }}
                >
                  Humanizing your text...
                </Typography>
                <Typography
                  variant="caption"
                  sx={{ color: "#94A3B8", mt: 0.5 }}
                >
                  This may take a moment
                </Typography>
              </Box>
            ) : humanizedResult ? (
              <Box
                sx={{
                  flex: 1,
                  display: "flex",
                  flexDirection: "column",
                  gap: 2,
                }}
              >
                <Paper
                  variant="outlined"
                  sx={{
                    flex: 1,
                    p: 2.5,
                    borderRadius: 3,
                    background:
                      "linear-gradient(135deg,rgba(16,185,129,0.04),rgba(5,150,105,0.02))",
                    borderColor: "rgba(16,185,129,0.2)",
                    overflow: "auto",
                    minHeight: 180,
                    maxHeight: 280,
                  }}
                >
                  <Typography
                    variant="body2"
                    sx={{
                      whiteSpace: "pre-wrap",
                      lineHeight: 1.8,
                      color: "#0F172A",
                    }}
                  >
                    {humanizedResult}
                  </Typography>
                </Paper>
                <Button
                  size="small"
                  startIcon={<ContentCopy sx={{ fontSize: 15 }} />}
                  onClick={handleCopyResult}
                  sx={{
                    alignSelf: "flex-start",
                    color: "#10B981",
                    border: "1.5px solid rgba(16,185,129,0.3)",
                    borderRadius: 2,
                    px: 2,
                    py: 0.6,
                    fontSize: "0.8rem",
                    fontWeight: 700,
                    "&:hover": {
                      background: "rgba(16,185,129,0.08)",
                      borderColor: "#10B981",
                    },
                  }}
                >
                  Copy Result
                </Button>
                <Box
                  sx={{
                    p: 2,
                    borderRadius: 2,
                    background: "rgba(241,245,255,0.8)",
                    border: "1px solid rgba(15,23,42,0.06)",
                  }}
                >
                  <Typography
                    variant="subtitle2"
                    sx={{ color: "#4a4a4a", mb: 1 }}
                  >
                    Word Count
                  </Typography>
                  <Box sx={{ display: "flex", gap: 3 }}>
                    <Box>
                      <Typography
                        variant="caption"
                        sx={{ color: "#3f3f3f", display: "block" }}
                      >
                        Original
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{ fontWeight: 700, color: "#4F6EF7" }}
                      >
                        {humanizeText.trim().split(/\s+/).length} words
                      </Typography>
                    </Box>
                    <Box
                      sx={{ width: "1px", background: "rgba(15,23,42,0.08)" }}
                    />
                    <Box>
                      <Typography
                        variant="caption"
                        sx={{ color: "#4c4c4c", display: "block" }}
                      >
                        Humanized
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{ fontWeight: 700, color: "#10B981" }}
                      >
                        {humanizedResult.trim().split(/\s+/).length} words
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              </Box>
            ) : (
              <Box
                sx={{
                  flex: 1,
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  borderRadius: 3,
                  background: "rgba(241,245,255,0.5)",
                  border: "1.5px dashed rgba(15,23,42,0.1)",
                  py: 6,
                  px: 3,
                  textAlign: "center",
                }}
              >
                <Box sx={{ fontSize: "2.5rem", mb: 1.5, opacity: 0.5 }}>✍️</Box>
                <Typography
                  variant="body2"
                  sx={{
                    color: "#94A3B8",
                    fontWeight: 500,
                    maxWidth: 200,
                    lineHeight: 1.6,
                  }}
                >
                  Your humanized text will appear here after processing.
                </Typography>
              </Box>
            )}
          </CardContent>
        </Card>
      </Grid>

      {/* Fullscreen Dialog */}
      <Dialog
        open={fullscreenDialogOpen}
        onClose={() => setFullscreenDialogOpen(false)}
        maxWidth="md"
        fullWidth
        fullScreen={isFullscreen}
        PaperProps={{ sx: { borderRadius: isFullscreen ? 0 : 3 } }}
      >
        <DialogTitle
          sx={{ borderBottom: "1px solid rgba(15,23,42,0.07)", py: 2 }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
              <AutoAwesome sx={{ color: "#10B981" }} />
              <Typography variant="h6" sx={{ color: "#0F172A" }}>
                Humanized Result — Full View
              </Typography>
            </Box>
            <Box sx={{ display: "flex", gap: 0.5 }}>
              <Button
                onClick={() => setIsFullscreen(!isFullscreen)}
                variant="contained"
                startIcon={isFullscreen ? <FullscreenExit /> : <Fullscreen />}
                sx={{
                  borderRadius: 2,
                  background: "linear-gradient(135deg,#4F6EF7,#A855F7)",
                }}
              >
                {isFullscreen ? "Exit Fullscreen" : "Fullscreen"}
              </Button>
              <IconButton
                size="small"
                onClick={() => setFullscreenDialogOpen(false)}
                sx={{
                  color: "#EF4444",
                  "&:hover": { background: "rgba(239,68,68,0.08)" },
                }}
              >
                <Close />
              </IconButton>
            </Box>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ p: 3 }}>
          {humanizedResult && (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
              <Paper
                variant="outlined"
                sx={{
                  p: 3,
                  borderRadius: 3,
                  background: "rgba(16,185,129,0.03)",
                  borderColor: "rgba(16,185,129,0.2)",
                  minHeight: 360,
                  maxHeight: isFullscreen ? "calc(100vh - 220px)" : 480,
                  overflow: "auto",
                }}
              >
                <Typography
                  variant="body1"
                  sx={{
                    whiteSpace: "pre-wrap",
                    wordBreak: "break-word",
                    lineHeight: 1.8,
                  }}
                >
                  {humanizedResult}
                </Typography>
              </Paper>
              <Box
                sx={{
                  p: 2.5,
                  borderRadius: 2,
                  background: "rgba(241,245,255,0.8)",
                  border: "1px solid rgba(15,23,42,0.06)",
                }}
              >
                <Typography
                  variant="subtitle2"
                  sx={{ mb: 2, color: "#424242" }}
                >
                  Text Statistics
                </Typography>
                <Grid container spacing={2}>
                  {[
                    {
                      label: "Original Words",
                      value: humanizeText.trim().split(/\s+/).length,
                      sub: `${humanizeText.length} chars`,
                      color: "#4F6EF7",
                    },
                    {
                      label: "Humanized Words",
                      value: humanizedResult.trim().split(/\s+/).length,
                      sub: `${humanizedResult.length} chars`,
                      color: "#10B981",
                    },
                  ].map((s) => (
                    <Grid xs={6} key={s.label}>
                      <Box
                        sx={{
                          p: 2,
                          borderRadius: 2,
                          background: "white",
                          border: "1px solid rgba(15,23,42,0.06)",
                          textAlign: "center",
                        }}
                      >
                        <Typography
                          variant="caption"
                          sx={{ color: "#474747", display: "block", mb: 0.5 }}
                        >
                          {s.label}
                        </Typography>
                        <Typography
                          variant="h5"
                          sx={{ fontWeight: 800, color: s.color }}
                        >
                          {s.value}
                        </Typography>
                        <Typography variant="caption" sx={{ color: "#3e3e3e" }}>
                          {s.sub}
                        </Typography>
                      </Box>
                    </Grid>
                  ))}
                </Grid>
              </Box>
            </Box>
          )}
        </DialogContent>
      </Dialog>
    </Grid>
  );
};

// ─── Image History Dialog Component ───────────────────────────────────────────
const ImageHistoryDialog = ({
  open,
  onClose,
  historyImages,
  onSelectImage,
  onDeleteFromHistory,
}) => {
  const safeHistoryImages = Array.isArray(historyImages) ? historyImages : [];

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          background: "linear-gradient(135deg, #FFFFFF 0%, #F8FAFF 100%)",
        },
      }}
    >
      <DialogTitle
        sx={{
          borderBottom: "1px solid rgba(15,23,42,0.07)",
          background: "linear-gradient(135deg, #8B5CF6 0%, #EC4899 100%)",
          color: "white",
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <History sx={{ fontSize: 24 }} />
            <Typography variant="h6" fontWeight="bold">
              Image History - Last {MAX_HISTORY_IMAGES} Images
            </Typography>
          </Box>
          <IconButton onClick={onClose} sx={{ color: "white" }}>
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent sx={{ p: 3 }}>
        {safeHistoryImages.length === 0 ? (
          <Box sx={{ textAlign: "center", py: 6 }}>
            <History
              sx={{ fontSize: 80, color: "rgba(139, 92, 246, 0.3)", mb: 2 }}
            />
            <Typography variant="h6" color="text.secondary">
              No Image History
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              Generate some images to see them here
            </Typography>
          </Box>
        ) : (
          <Grid container spacing={2} sx={{ mt: 1 }}>
            {safeHistoryImages.map((image, index) => (
              <Grid xs={12} sm={6} md={4} key={image.id}>
                <Paper
                  elevation={2}
                  sx={{
                    position: "relative",
                    overflow: "hidden",
                    borderRadius: 2,
                    cursor: "pointer",
                    transition: "all 0.3s",
                    border: "2px solid transparent",
                    "&:hover": {
                      transform: "scale(1.02)",
                      boxShadow: "0 8px 20px rgba(139, 92, 246, 0.3)",
                      border: "2px solid #8B5CF6",
                    },
                  }}
                  onClick={() => onSelectImage(image)}
                >
                  <Box sx={{ position: "relative", paddingTop: "75%" }}>
                    <img
                      src={image.url}
                      alt={`History ${index + 1}`}
                      style={{
                        position: "absolute",
                        top: 0,
                        left: 0,
                        width: "100%",
                        height: "100%",
                        objectFit: "cover",
                      }}
                      onError={(e) => {
                        e.target.src =
                          "https://via.placeholder.com/400x300?text=Image+Load+Error";
                      }}
                    />
                    <Chip
                      label={image.style || "Unknown Style"}
                      size="small"
                      sx={{
                        position: "absolute",
                        bottom: 8,
                        left: 8,
                        bgcolor: "rgba(139, 92, 246, 0.9)",
                        color: "white",
                        textTransform: "capitalize",
                        fontWeight: 500,
                        fontSize: "0.65rem",
                      }}
                    />
                    <Chip
                      icon={<Schedule sx={{ fontSize: 12 }} />}
                      label={
                        image.timestamp
                          ? new Date(image.timestamp).toLocaleDateString()
                          : "Unknown Date"
                      }
                      size="small"
                      sx={{
                        position: "absolute",
                        top: 8,
                        right: 8,
                        bgcolor: "rgba(0,0,0,0.6)",
                        color: "white",
                        fontSize: "0.6rem",
                      }}
                    />
                    <IconButton
                      size="small"
                      sx={{
                        position: "absolute",
                        bottom: 8,
                        right: 8,
                        bgcolor: "rgba(0,0,0,0.6)",
                        color: "white",
                        "&:hover": { bgcolor: "#EF4444" },
                        width: 24,
                        height: 24,
                      }}
                      onClick={(e) => {
                        e.stopPropagation();
                        if (onDeleteFromHistory) onDeleteFromHistory(image.id);
                      }}
                    >
                      <Delete sx={{ fontSize: 14 }} />
                    </IconButton>
                  </Box>
                  <Box sx={{ p: 1.5, bgcolor: "rgba(139, 92, 246, 0.05)" }}>
                    <Typography
                      variant="caption"
                      sx={{
                        display: "block",
                        color: "text.secondary",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {image.prompt
                        ? image.prompt.substring(0, 60)
                        : "No prompt available"}
                      {image.prompt && image.prompt.length > 60 ? "..." : ""}
                    </Typography>
                  </Box>
                </Paper>
              </Grid>
            ))}
          </Grid>
        )}
      </DialogContent>
      <DialogActions sx={{ p: 2, borderTop: "1px solid rgba(15,23,42,0.07)" }}>
        <Button onClick={onClose} variant="outlined" sx={{ borderRadius: 2 }}>
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// ─── AI GenArt Studio Component (Updated with proper history management) ───
const AIGenArtStudio = ({
  accessToAIGenArtStudio,
  accessToArtStyle,
  userID,
}) => {
  const [artStyle, setArtStyle] = useState("realistic");
  const [imagePrompt, setImagePrompt] = useState("");
  const [generatedImages, setGeneratedImages] = useState([]);
  const [isGenerating, setIsGenerating] = useState(false);
  const [fullscreenGalleryOpen, setFullscreenGalleryOpen] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [error, setError] = useState(null);
  const [historyDialogOpen, setHistoryDialogOpen] = useState(false);
  const [imageHistory, setImageHistory] = useState([]);
  const [activeImageSource, setActiveImageSource] = useState(null);

  const generateId = () =>
    crypto.randomUUID
      ? crypto.randomUUID()
      : Date.now().toString() + Math.random().toString(36).slice(2, 10);

  // Load image history from localStorage on component mount
  useEffect(() => {
    const savedHistory = localStorage.getItem("aiImageHistory");
    if (savedHistory) {
      try {
        const parsedHistory = JSON.parse(savedHistory);
        if (Array.isArray(parsedHistory)) {
          const historyWithDates = parsedHistory.map((img) => ({
            ...img,
            timestamp: img.timestamp ? new Date(img.timestamp) : new Date(),
          }));
          setImageHistory(historyWithDates);
        } else {
          setImageHistory([]);
        }
      } catch (error) {
        console.error("Error loading image history:", error);
        setImageHistory([]);
      }
    } else {
      setImageHistory([]);
    }
  }, []);

  // Save image history to localStorage with FIFO management
  const saveHistoryToLocalStorage = (history) => {
    try {
      const historyToSave = history.slice(0, MAX_HISTORY_IMAGES); // Keep only last 5
      localStorage.setItem("aiImageHistory", JSON.stringify(historyToSave));
      return historyToSave;
    } catch (error) {
      if (error.name === "QuotaExceededError") {
        console.error("Storage quota exceeded");
        toast.error(
          "Storage limit reached. Please clear some images from history.",
          {
            position: "top-center",
            autoClose: 5000,
          },
        );
        return history.slice(0, 3); // Try to keep only last 3
      }
      return history;
    }
  };

  // Add image to history - maintains last 5 images (FIFO)
  const addToHistory = (image) => {
    setImageHistory((prev) => {
      const currentHistory = Array.isArray(prev) ? prev : [];
      const newHistory = [image, ...currentHistory];
      // Keep only last MAX_HISTORY_IMAGES
      return newHistory.slice(0, MAX_HISTORY_IMAGES);
    });
  };

  const handleGenerateImages = async () => {
    if (!imagePrompt.trim()) {
      toast.warning("Please describe the image you want to generate", {
        position: "top-center",
        autoClose: 3000,
      });
      return;
    }

    setIsGenerating(true);
    setError(null);

    try {
      const response = await generateAIImage(imagePrompt, artStyle);

      if (response.data && response.data.image_base64) {
        const imageUrl = `data:image/png;base64,${response.data.image_base64}`;

        const newImage = {
          id: generateId(),
          url: imageUrl,
          prompt: imagePrompt,
          style: artStyle,
          timestamp: new Date(),
          isHistory: false,
        };

        setGeneratedImages([newImage]);
        setActiveImageSource("generated");

        // Add to history (this will automatically maintain last 5 images)
        addToHistory(newImage);

        const logData = {
          userId: sessionStorage.getItem("userId"),
          organizationId: sessionStorage.getItem("organizationId"),
          actionType: "imageGenerator",
        };
        await addUserLogs(logData);

        toast.success("Image generated successfully!", {
          position: "top-center",
          autoClose: 3000,
        });
      } else {
        throw new Error("Invalid response format from API");
      }
    } catch (error) {
      console.error("Error generating images:", error);
      const errorMessage =
        error.response?.data?.message ||
        error.message ||
        "Failed to generate images. Please try again.";
      setError(errorMessage);
      toast.error(errorMessage, {
        position: "top-center",
        autoClose: 5000,
      });
    } finally {
      setIsGenerating(false);
    }
  };

  const handleSelectHistoryImage = (historyImage) => {
    const selectedImage = {
      ...historyImage,
      isHistory: true,
      id: historyImage.id + "_history_" + Date.now(),
    };
    setGeneratedImages([selectedImage]);
    setActiveImageSource("history");
    setHistoryDialogOpen(false);
    toast.success("Image loaded from history!", {
      position: "top-center",
      autoClose: 2000,
    });
  };

  const handleDeleteFromHistory = (imageId) => {
    setImageHistory((prev) => {
      const currentHistory = Array.isArray(prev) ? prev : [];
      const updatedHistory = currentHistory.filter((img) => img.id !== imageId);

      try {
        if (updatedHistory.length > 0) {
          localStorage.setItem(
            "aiImageHistory",
            JSON.stringify(updatedHistory),
          );
        } else {
          localStorage.removeItem("aiImageHistory");
        }
        toast.success("Image removed from history!", {
          position: "top-center",
          autoClose: 2000,
        });
      } catch (error) {
        console.error("Error updating history:", error);
        toast.error("Failed to remove image from history", {
          position: "top-center",
          autoClose: 3000,
        });
      }

      return updatedHistory;
    });
  };

  const handleClearHistory = () => {
    setImageHistory([]);
    localStorage.removeItem("aiImageHistory");
    toast.success("History cleared!", {
      position: "top-center",
      autoClose: 2000,
    });
  };

  const handleDownloadImage = async (imageUrl, index) => {
    try {
      if (imageUrl.startsWith("data:image")) {
        const link = document.createElement("a");
        link.href = imageUrl;
        link.download = `ai-generated-image-${index + 1}.png`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      } else {
        const response = await fetch(imageUrl);
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = `ai-generated-image-${index + 1}.jpg`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      }

      toast.success("Image downloaded successfully!", {
        position: "top-center",
        autoClose: 2000,
      });
    } catch (error) {
      console.error("Error downloading image:", error);
      toast.error("Failed to download image", {
        position: "top-center",
        autoClose: 3000,
      });
    }
  };

  const handleCopyImage = async (imageUrl) => {
    try {
      if (imageUrl.startsWith("data:image")) {
        const base64Data = imageUrl.split(",")[1];
        const byteCharacters = atob(base64Data);
        const byteNumbers = new Array(byteCharacters.length);
        for (let i = 0; i < byteCharacters.length; i++) {
          byteNumbers[i] = byteCharacters.charCodeAt(i);
        }
        const byteArray = new Uint8Array(byteNumbers);
        const blob = new Blob([byteArray], { type: "image/png" });

        await navigator.clipboard.write([
          new ClipboardItem({
            [blob.type]: blob,
          }),
        ]);
      } else {
        const response = await fetch(imageUrl);
        const blob = await response.blob();
        await navigator.clipboard.write([
          new ClipboardItem({
            [blob.type]: blob,
          }),
        ]);
      }

      toast.success("Image copied to clipboard!", {
        position: "top-center",
        autoClose: 2000,
      });
    } catch (error) {
      console.error("Error copying image:", error);
      toast.error("Failed to copy image", {
        position: "top-center",
        autoClose: 3000,
      });
    }
  };

  const handleOpenFullscreenGallery = (index = 0) => {
    setCurrentImageIndex(index);
    setFullscreenGalleryOpen(true);
  };

  const handleDeleteImage = (imageId) => {
    setGeneratedImages((prev) => prev.filter((img) => img.id !== imageId));
    setActiveImageSource(null);
    toast.success("Image removed from gallery", {
      position: "top-center",
      autoClose: 2000,
    });
  };

  const handleClearAll = () => {
    setGeneratedImages([]);
    setActiveImageSource(null);
    toast.success("Gallery cleared", {
      position: "top-center",
      autoClose: 2000,
    });
  };

  // Check if user has access to AI GenArt Studio
  if (!accessToAIGenArtStudio) {
    return (
      <Grid container spacing={2}>
        <Grid xs={12}>
          <Card>
            <CardContent>
              <Box textAlign="center" py={4}>
                <ImageIcon
                  sx={{ fontSize: 64, color: "text.primary", mb: 2 }}
                />
                <Typography variant="h6" color="text.primary" gutterBottom>
                  AI GenArt Studio Not Available
                </Typography>
                <Typography variant="body2" color="text.primary">
                  This feature is not included in your current plan. Upgrade to
                  access AI image generation.
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    );
  }

  return (
    <>
      <Grid container spacing={3} sx={{ height: "calc(100vh - 200px)" }}>
        {/* Left Column - Input */}
        <Grid xs={12} md={7.2} lg={7.2} sx={{ height: "100%" }}>
          <Card
            sx={{ height: "100%", display: "flex", flexDirection: "column" }}
          >
            <CardContent
              sx={{ flexGrow: 1, display: "flex", flexDirection: "column" }}
            >
              <Box
                display="flex"
                alignItems="center"
                justifyContent="space-between"
                mb={2}
              >
                <Box display="flex" alignItems="center" gap={1}>
                  <Box
                    sx={{
                      width: 40,
                      height: 40,
                      borderRadius: 2,
                      background: "linear-gradient(135deg,#8B5CF6,#EC4899)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <ImageIcon sx={{ color: "white", fontSize: 20 }} />
                  </Box>
                  <Typography variant="h6">AI Image Generator</Typography>
                </Box>
                <Box display="flex" gap={1}>
                  {/* History Button */}
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<History />}
                    onClick={() => setHistoryDialogOpen(true)}
                    disabled={imageHistory.length === 0}
                    sx={{
                      borderColor:
                        imageHistory.length === 0
                          ? "rgba(15,23,42,0.2)"
                          : "#8B5CF6",
                      color: imageHistory.length === 0 ? "#64748B" : "#8B5CF6",
                      fontWeight: 600,
                      "&:hover": {
                        borderColor: "#8B5CF6",
                        background: "rgba(139, 92, 246, 0.08)",
                      },
                    }}
                  >
                    History ({imageHistory.length}/{MAX_HISTORY_IMAGES})
                  </Button>

                  {/* {imageHistory.length > 0 && (
                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={<Clear />}
                      onClick={handleClearHistory}
                      sx={{
                        borderColor: "rgba(239,68,68,0.3)",
                        color: "#EF4444",
                        fontWeight: 600,
                        '&:hover': {
                          borderColor: "#EF4444",
                          background: "rgba(239,68,68,0.08)",
                        },
                      }}
                    >
                      Clear History
                    </Button>
                  )} */}

                  {generatedImages.length > 0 && (
                    <>
                      <Button
                        variant="contained"
                        size="small"
                        startIcon={<Collections />}
                        onClick={() => handleOpenFullscreenGallery(0)}
                        sx={{
                          background:
                            "linear-gradient(90deg, #8B5CF6 0%, #EC4899 100%)",
                          color: "white",
                          fontWeight: "bold",
                          "&:hover": {
                            background:
                              "linear-gradient(90deg, #7C3AED 0%, #DB2777 100%)",
                          },
                        }}
                      >
                        Fullscreen Gallery
                      </Button>
                      <Button
                        variant="outlined"
                        size="small"
                        startIcon={<Clear />}
                        onClick={handleClearAll}
                        sx={{
                          borderColor: "rgba(15,23,42,0.2)",
                          color: "#64748B",
                        }}
                      >
                        Clear
                      </Button>
                    </>
                  )}
                </Box>
              </Box>

              <Typography
                variant="subtitle2"
                gutterBottom
                sx={{ color: "#64748B" }}
              >
                Describe your image
              </Typography>

              <TextField
                fullWidth
                multiline
                rows={10}
                placeholder="A beautiful sunset over mountains with vibrant colors..."
                variant="outlined"
                value={imagePrompt}
                onChange={(e) => setImagePrompt(e.target.value)}
                sx={{ mb: 2, flexGrow: 1 }}
                disabled={isGenerating}
              />

              <Typography
                variant="subtitle2"
                gutterBottom
                sx={{ color: "#64748B" }}
              >
                Art Style
              </Typography>

              <ToggleButtonGroup
                value={artStyle}
                exclusive
                onChange={(e, val) =>
                  val && accessToArtStyle && setArtStyle(val)
                }
                fullWidth
                sx={{ mb: 2 }}
              >
                <ToggleButton
                  value="realistic"
                  disabled={!accessToArtStyle || isGenerating}
                >
                  <Box textAlign="center">
                    <PhotoCamera />
                    <Typography variant="caption" display="block">
                      Realistic
                      {!accessToArtStyle && " (Premium)"}
                    </Typography>
                  </Box>
                </ToggleButton>

                <ToggleButton
                  value="artistic"
                  disabled={!accessToArtStyle || isGenerating}
                >
                  <Box textAlign="center">
                    <Brush />
                    <Typography variant="caption" display="block">
                      Artistic
                      {!accessToArtStyle && " (Premium)"}
                    </Typography>
                  </Box>
                </ToggleButton>

                <ToggleButton
                  value="cartoon"
                  disabled={!accessToArtStyle || isGenerating}
                >
                  <Box textAlign="center">
                    <EmojiEmotions />
                    <Typography variant="caption" display="block">
                      Cartoon
                      {!accessToArtStyle && " (Premium)"}
                    </Typography>
                  </Box>
                </ToggleButton>

                <ToggleButton
                  value="abstract"
                  disabled={!accessToArtStyle || isGenerating}
                >
                  <Box textAlign="center">
                    <ViewComfy />
                    <Typography variant="caption" display="block">
                      Abstract
                      {!accessToArtStyle && " (Premium)"}
                    </Typography>
                  </Box>
                </ToggleButton>
              </ToggleButtonGroup>

              <Button
                fullWidth
                variant="contained"
                size="large"
                startIcon={<AutoAwesome />}
                onClick={handleGenerateImages}
                disabled={isGenerating || !imagePrompt.trim()}
                sx={{
                  background:
                    "linear-gradient(90deg, #8B5CF6 0%, #EC4899 100%)",
                  py: 1.6,
                  borderRadius: 2,
                  fontWeight: 800,
                  fontSize: "0.95rem",
                  "&:disabled": {
                    background: "rgba(15,23,42,0.08)",
                  },
                }}
              >
                {isGenerating ? "Generating..." : "Generate Images"}
              </Button>

              {error && (
                <Alert severity="error" sx={{ mt: 2 }}>
                  {error}
                </Alert>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Right Column - Output */}
        <Grid xs={12} md={4.8} lg={4.8} sx={{ height: "100%" }}>
          <Card
            sx={{ height: "100%", display: "flex", flexDirection: "column" }}
          >
            <CardContent
              sx={{
                flexGrow: 1,
                display: "flex",
                flexDirection: "column",
                overflow: "auto",
              }}
            >
              <Box
                display="flex"
                alignItems="center"
                justifyContent="space-between"
                mb={2}
              >
                <Box display="flex" alignItems="center" gap={1}>
                  <AutoAwesome sx={{ color: "#8B5CF6" }} />
                  <Typography variant="h6">Generated Images</Typography>
                  {activeImageSource === "history" &&
                    generatedImages.length > 0 && (
                      <Chip
                        label="From History"
                        size="small"
                        sx={{
                          bgcolor: "#F59E0B",
                          color: "white",
                          fontWeight: 600,
                          fontSize: "0.7rem",
                        }}
                      />
                    )}
                  {activeImageSource === "generated" &&
                    generatedImages.length > 0 && (
                      <Chip
                        label="Newly Generated"
                        size="small"
                        sx={{
                          bgcolor: "#10B981",
                          color: "white",
                          fontWeight: 600,
                          fontSize: "0.7rem",
                        }}
                      />
                    )}
                </Box>
                {generatedImages.length > 0 && (
                  <Box display="flex" alignItems="center" gap={1}>
                    <Chip
                      icon={<Collections />}
                      label={`${generatedImages.length} image${generatedImages.length > 1 ? "s" : ""}`}
                      size="small"
                      sx={{
                        bgcolor: "rgba(139, 92, 246, 0.1)",
                        color: "#8B5CF6",
                        fontWeight: 500,
                      }}
                    />
                    <IconButton
                      size="small"
                      onClick={() => handleOpenFullscreenGallery(0)}
                      sx={{
                        bgcolor: "#8B5CF6",
                        color: "white",
                        "&:hover": {
                          bgcolor: "#7C3AED",
                        },
                      }}
                      title="Open fullscreen gallery"
                    >
                      <Fullscreen fontSize="small" />
                    </IconButton>
                  </Box>
                )}
              </Box>

              {isGenerating ? (
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    flexGrow: 1,
                  }}
                >
                  <CircularProgress
                    size={60}
                    sx={{ mb: 3, color: "#8B5CF6" }}
                  />
                  <Typography variant="h6" gutterBottom>
                    Creating your masterpiece...
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    AI is generating an image based on your description
                  </Typography>
                </Box>
              ) : generatedImages.length > 0 ? (
                <Box sx={{ flexGrow: 1, overflow: "auto", pr: 1 }}>
                  <Grid container spacing={2}>
                    {generatedImages.map((image, index) => (
                      <Grid xs={12} sm={6} key={image.id}>
                        <Paper
                          elevation={2}
                          sx={{
                            position: "relative",
                            overflow: "hidden",
                            borderRadius: 2,
                            transition: "transform 0.2s",
                            border: image.isHistory
                              ? "2px solid #F59E0B"
                              : "1px solid rgba(139, 92, 246, 0.2)",
                            height: "100%",
                            display: "flex",
                            flexDirection: "column",
                            "&:hover": {
                              transform: "scale(1.02)",
                              boxShadow: "0 8px 20px rgba(139, 92, 246, 0.3)",
                              "& .image-actions": {
                                opacity: 1,
                              },
                            },
                          }}
                        >
                          <Box
                            sx={{
                              position: "relative",
                              width: "100%",
                              paddingTop: "75%",
                              backgroundColor: "#f5f5f5",
                              overflow: "hidden",
                            }}
                          >
                            <img
                              src={image.url}
                              alt={`Generated ${index + 1}`}
                              style={{
                                position: "absolute",
                                top: 0,
                                left: 0,
                                width: "100%",
                                height: "100%",
                                objectFit: "cover",
                                cursor: "pointer",
                              }}
                              onClick={() => handleOpenFullscreenGallery(index)}
                              onError={(e) => {
                                e.target.src =
                                  "https://via.placeholder.com/400x300?text=Image+Load+Error";
                              }}
                            />

                            <Box
                              className="image-actions"
                              sx={{
                                position: "absolute",
                                top: 8,
                                right: 8,
                                display: "flex",
                                gap: 0.5,
                                opacity: 0,
                                transition: "opacity 0.2s",
                                backgroundColor: "rgba(0,0,0,0.6)",
                                borderRadius: 2,
                                p: 0.5,
                                zIndex: 2,
                              }}
                            >
                              <IconButton
                                size="small"
                                sx={{
                                  bgcolor: "rgba(255,255,255,0.2)",
                                  color: "white",
                                  "&:hover": {
                                    bgcolor: "rgba(139, 92, 246, 0.8)",
                                  },
                                  width: 28,
                                  height: 28,
                                }}
                                onClick={() =>
                                  handleDownloadImage(image.url, index)
                                }
                                title="Download image"
                              >
                                <Download fontSize="small" />
                              </IconButton>
                              <IconButton
                                size="small"
                                sx={{
                                  bgcolor: "rgba(255,255,255,0.2)",
                                  color: "white",
                                  "&:hover": {
                                    bgcolor: "rgba(139, 92, 246, 0.8)",
                                  },
                                  width: 28,
                                  height: 28,
                                }}
                                onClick={() => handleCopyImage(image.url)}
                                title="Copy to clipboard"
                              >
                                <ContentCopy fontSize="small" />
                              </IconButton>
                              <IconButton
                                size="small"
                                sx={{
                                  bgcolor: "rgba(139, 92, 246, 0.8)",
                                  color: "white",
                                  "&:hover": { bgcolor: "#8B5CF6" },
                                  width: 28,
                                  height: 28,
                                }}
                                onClick={() =>
                                  handleOpenFullscreenGallery(index)
                                }
                                title="View fullscreen"
                              >
                                <Fullscreen fontSize="small" />
                              </IconButton>
                            </Box>

                            <Chip
                              label={image.style}
                              size="small"
                              sx={{
                                position: "absolute",
                                bottom: 8,
                                left: 8,
                                bgcolor: "rgba(139, 92, 246, 0.9)",
                                color: "white",
                                textTransform: "capitalize",
                                fontWeight: 500,
                                fontSize: "0.65rem",
                                height: 20,
                                zIndex: 2,
                              }}
                            />

                            <Chip
                              label={image.isHistory ? "📜 History" : "✨ New"}
                              size="small"
                              sx={{
                                position: "absolute",
                                top: 8,
                                left: 8,
                                bgcolor: image.isHistory
                                  ? "#F59E0B"
                                  : "#10B981",
                                color: "white",
                                fontWeight: 600,
                                fontSize: "0.65rem",
                                height: 20,
                                zIndex: 2,
                              }}
                            />
                          </Box>

                          <Box
                            sx={{
                              p: 1,
                              backgroundColor: image.isHistory
                                ? "rgba(245, 158, 11, 0.05)"
                                : "rgba(139, 92, 246, 0.05)",
                              borderTop: image.isHistory
                                ? "1px solid rgba(245, 158, 11, 0.2)"
                                : "1px solid rgba(139, 92, 246, 0.1)",
                            }}
                          >
                            <Typography
                              variant="caption"
                              sx={{
                                display: "block",
                                color: "text.secondary",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                              }}
                            >
                              {image.prompt.substring(0, 50)}
                              {image.prompt.length > 50 ? "..." : ""}
                            </Typography>
                          </Box>
                        </Paper>
                      </Grid>
                    ))}
                  </Grid>
                </Box>
              ) : (
                <Paper
                  variant="outlined"
                  sx={{
                    p: 4,
                    textAlign: "center",
                    bgcolor: "rgba(139, 92, 246, 0.03)",
                    flexGrow: 1,
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    border: "2px dashed rgba(139, 92, 246, 0.3)",
                  }}
                >
                  <ImageIcon
                    sx={{
                      fontSize: 80,
                      color: "rgba(139, 92, 246, 0.3)",
                      mb: 2,
                    }}
                  />
                  <Typography variant="h6" color="text.primary" gutterBottom>
                    No Images Generated Yet
                  </Typography>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ maxWidth: 300 }}
                  >
                    Describe the image on the left and click "Generate Images"
                    to create stunning AI artwork
                  </Typography>
                  {imageHistory.length > 0 && (
                    <Button
                      variant="outlined"
                      startIcon={<History />}
                      onClick={() => setHistoryDialogOpen(true)}
                      sx={{ mt: 2 }}
                    >
                      View History ({imageHistory.length} images)
                    </Button>
                  )}
                </Paper>
              )}

              {generatedImages.length > 0 && (
                <Box
                  sx={{
                    mt: 2,
                    p: 2,
                    bgcolor: "rgba(139, 92, 246, 0.05)",
                    borderRadius: 2,
                  }}
                >
                  <Typography
                    variant="caption"
                    display="block"
                    gutterBottom
                    sx={{ fontWeight: 500 }}
                  >
                    {activeImageSource === "history"
                      ? "Image from History:"
                      : "Generation Details:"}
                  </Typography>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      flexWrap: "wrap",
                      gap: 1,
                    }}
                  >
                    <Typography
                      variant="caption"
                      color="text.primary"
                      sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
                    >
                      <AutoAwesome sx={{ fontSize: 14, color: "#8B5CF6" }} />
                      Prompt: {generatedImages[0].prompt.substring(0, 40)}
                      {generatedImages[0].prompt.length > 40 ? "..." : ""}
                    </Typography>
                    <Chip
                      label={generatedImages[0].style}
                      size="small"
                      sx={{
                        bgcolor: "#8B5CF6",
                        color: "white",
                        fontSize: "0.65rem",
                        height: 20,
                      }}
                    />
                  </Box>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <FullscreenImageGallery
        open={fullscreenGalleryOpen}
        onClose={() => setFullscreenGalleryOpen(false)}
        images={generatedImages}
        currentImageIndex={currentImageIndex}
        onIndexChange={setCurrentImageIndex}
        onDownload={handleDownloadImage}
        onCopy={handleCopyImage}
        onDelete={handleDeleteImage}
      />

      <ImageHistoryDialog
        open={historyDialogOpen}
        onClose={() => setHistoryDialogOpen(false)}
        historyImages={imageHistory}
        onSelectImage={handleSelectHistoryImage}
        onDeleteFromHistory={handleDeleteFromHistory}
      />
    </>
  );
};

// ─── Main App (Rest of the code remains the same) ───
export default function AIIntelligenzApp() {
  const userDetails = useSelector((state) => state.user);
  const userID =
    userDetails?.currentUser?._id || userDetails?.user?.currentUser?._id;
  const userPlan = userDetails?.currentUser?.plan || "basic";

  const accessToAIGenArtStudio = useCheckFeatureAccess(
    "aiIntelligenz",
    "genArtStudio",
  );
  const accessToArtStyle = useCheckFeatureAccess("aiIntelligenz", "artStyle");
  const accessToAISmartDesk = useCheckFeatureAccess(
    "aiIntelligenz",
    "smartDesk",
  );
  const accessToHumanizer = useCheckFeatureAccess("aiIntelligenz", "humanizer");
  const accessToLightLevel = useCheckFeatureAccess(
    "aiIntelligenz",
    "humanizerLight",
  );
  const accessToBalancedLevel = useCheckFeatureAccess(
    "aiIntelligenz",
    "humanizerBalanced",
  );
  const accessToMaximumLevel = useCheckFeatureAccess(
    "aiIntelligenz",
    "humanizerMaximum",
  );
  const accessToAIIntelligenzScreen =
    useCheckSubscriptionPlanAccess("AI Image Generator");
  const [subReqOpen, setSubReqOpen] = useState(false);
  const [smartDeskMessages, setSmartDeskMessages] = useState([]); // displayed chat bubbles
  const [conversationHistory, setConversationHistory] = useState([]); // sent to API
  const [smartDeskInput, setSmartDeskInput] = useState("");
  const [smartDeskLoading, setSmartDeskLoading] = useState(false);
  const chatBottomRef = useRef(null);

  useEffect(() => {
    chatBottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [smartDeskMessages]);

  useEffect(() => {
    if (accessToAIIntelligenzScreen === false) setSubReqOpen(true);
  }, [accessToAIIntelligenzScreen]);

  useEffect(() => {
    const html = document.documentElement;
    const body = document.body;
    const previousHtmlOverflow = html.style.overflow;
    const previousBodyOverflow = body.style.overflow;
    const previousHtmlHeight = html.style.height;
    const previousBodyHeight = body.style.height;

    html.style.overflow = "hidden";
    body.style.overflow = "hidden";
    html.style.height = "100%";
    body.style.height = "100%";

    return () => {
      html.style.overflow = previousHtmlOverflow;
      body.style.overflow = previousBodyOverflow;
      html.style.height = previousHtmlHeight;
      body.style.height = previousBodyHeight;
    };
  }, []);

  const [activeTab, setActiveTab] = useState("chatbot");
  const [humanizerLevel, setHumanizerLevel] = useState("light");
  const [chatMessage, setChatMessage] = useState("");
  const [conversation, setConversation] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [chatHistory, setChatHistory] = useState([]);
  const [currentChatId, setCurrentChatId] = useState(null);
  const [messageMenuAnchor, setMessageMenuAnchor] = useState(null);
  const [selectedMessage, setSelectedMessage] = useState(null);
  const [editingMessage, setEditingMessage] = useState(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editMessageContent, setEditMessageContent] = useState("");
  const [chatMenuAnchor, setChatMenuAnchor] = useState(null);
  const [selectedChat, setSelectedChat] = useState(null);
  const [renameDialogOpen, setRenameDialogOpen] = useState(false);
  const [newChatTitle, setNewChatTitle] = useState("");
  const [showSuccess, setShowSuccess] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");

  const getChatWordLimit = () =>
    userPlan === "premium" ? MAX_CHAT_WORDS_PREMIUM : MAX_CHAT_WORDS;
  const generateId = () =>
    crypto.randomUUID
      ? crypto.randomUUID()
      : Date.now().toString() + Math.random().toString(36).slice(2, 10);
  const messagesEndRef = useRef(null);
  const messagesContainerRef = useRef(null);

  const showToast = (msg, sev = "info") => {
    setSuccessMessage(msg);
    if (sev === "success" || sev === "info") setShowSuccess(true);
  };

  useEffect(() => {
    const container = messagesContainerRef.current;
    if (!container) return;
    container.scrollTo({
      top: container.scrollHeight,
      behavior: "smooth",
    });
  }, [conversation, isLoading]);

  useEffect(() => {
    const savedChats = localStorage.getItem("aiChatHistory");
    if (savedChats) {
      try {
        const parsedChats = JSON.parse(savedChats).map((chat) => ({
          ...chat,
          timestamp: new Date(chat.timestamp),
          messages: chat.messages.map((msg) => ({
            ...msg,
            timestamp: new Date(msg.timestamp),
          })),
        }));
        setChatHistory(parsedChats);
      } catch {
        setChatHistory([]);
      }
    }
  }, []);

  useEffect(() => {
    if (chatHistory.length > 0)
      localStorage.setItem("aiChatHistory", JSON.stringify(chatHistory));
    else localStorage.removeItem("aiChatHistory");
  }, [chatHistory]);

  const saveChatToHistory = (messages, title = "New Chat") => {
    const newChat = {
      id: generateId(),
      title,
      messages: [...messages],
      timestamp: new Date(),
      pinned: false,
    };
    setChatHistory((prev) => [newChat, ...prev]);
    setCurrentChatId(newChat.id);
    return newChat.id;
  };

  const updateChatHistory = (chatId, updatedMessages, updatedTitle = null) => {
    setChatHistory((prev) =>
      prev.map((chat) =>
        chat.id === chatId
          ? {
              ...chat,
              messages: updatedMessages,
              title: updatedTitle ?? chat.title,
              timestamp: new Date(),
            }
          : chat,
      ),
    );
  };

  const handleSendMessage = async (
    message = chatMessage,
    isQuickPrompt = false,
  ) => {
    if (!accessToAISmartDesk) {
      toast.warn("AI SmartDesk feature is not available in your plan");
      return;
    }
    const finalMessage = message?.trim() || chatMessage?.trim();
    if (!finalMessage) {
      toast.warning("Please enter a message");
      return;
    }
    const wordCount = finalMessage.split(/\s+/).length;
    if (wordCount > getChatWordLimit()) {
      toast.warn(`Message exceeds ${getChatWordLimit()} word limit`);
      return;
    }

    const userMessage = {
      id: generateId(),
      type: "user",
      content: finalMessage,
      timestamp: new Date(),
    };
    const updatedConversation = [...conversation, userMessage];
    setConversation(updatedConversation);
    setChatMessage("");
    setIsLoading(true);

    let chatId = currentChatId;
    if (!currentChatId) {
      const firstFewWords = finalMessage.split(" ").slice(0, 5).join(" ");
      chatId = saveChatToHistory(
        [userMessage],
        firstFewWords + (finalMessage.split(" ").length > 5 ? "..." : ""),
      );
    } else {
      updateChatHistory(currentChatId, updatedConversation);
    }

    try {
      const historyForAPI = conversation.map((msg) => ({
        role: msg.type === "user" ? "user" : "assistant",
        content: msg.content,
      }));
      const response = await aiSmartDesk(finalMessage, "", historyForAPI);
      const aiResponseText =
        response.data?.data?.response ||
        response.data?.response ||
        (typeof response.data === "string" ? response.data : null) ||
        response?.response ||
        "Received response but couldn't parse it.";
      const aiMessage = {
        id: generateId(),
        type: "ai",
        content: aiResponseText,
        timestamp: new Date(),
      };
      const finalConversation = [...updatedConversation, aiMessage];
      setConversation(finalConversation);
      updateChatHistory(chatId, finalConversation);
      await addUserLogs({
        userId: sessionStorage.getItem("userId"),
        organizationId: sessionStorage.getItem("organizationId"),
        actionType: "smartDesk",
      });
    } catch (error) {
      const errorMessage = {
        id: generateId(),
        type: "ai",
        content:
          "Sorry, something went wrong. Error: " +
          (error.response?.data?.message || error.message),
        timestamp: new Date(),
        isError: true,
      };
      const finalConversation = [...updatedConversation, errorMessage];
      setConversation(finalConversation);
      if (chatId) updateChatHistory(chatId, finalConversation);
    } finally {
      setIsLoading(false);
    }
  };

  const handleMessageMenuOpen = (event, message) => {
    setMessageMenuAnchor(event.currentTarget);
    setSelectedMessage(message);
  };
  const handleMessageMenuClose = () => {
    setMessageMenuAnchor(null);
    setSelectedMessage(null);
  };
  const handleCopyMessage = () => {
    if (selectedMessage) {
      navigator.clipboard.writeText(selectedMessage.content);
      showToast("Message copied!", "success");
      handleMessageMenuClose();
    }
  };
  const handleEditMessage = () => {
    if (selectedMessage) {
      setEditingMessage(selectedMessage);
      setEditMessageContent(selectedMessage.content);
      setEditDialogOpen(true);
      handleMessageMenuClose();
    }
  };

  const handleSmartDeskSend = async () => {
    const text = smartDeskInput.trim();
    if (!text || smartDeskLoading) return;

    // Optimistically add the user bubble
    const userBubble = { role: "user", content: text, ts: Date.now() };
    setSmartDeskMessages((prev) => [...prev, userBubble]);
    setSmartDeskInput("");
    setSmartDeskLoading(true);

    try {
      // const result = await aiSmartDesk({
      //   message: text,
      //   conversationHistory,
      // });

      // const reply = result?.data?.response || "No response received.";
      // const updatedHistory = result?.data?.conversationHistory || [];

      const result = await aiSmartDesk(text, "", conversationHistory);
      const reply =
        result?.data?.data?.response ||
        result?.data?.response ||
        "No response received.";
      const updatedHistory =
        result?.data?.data?.conversationHistory ||
        result?.data?.conversationHistory ||
        [];
      setConversationHistory(updatedHistory);
      setSmartDeskMessages((prev) => [
        ...prev,
        { role: "assistant", content: reply, ts: Date.now() },
      ]);
    } catch (err) {
      setSmartDeskMessages((prev) => [
        ...prev,
        {
          role: "assistant",
          content: "Something went wrong. Please try again.",
          ts: Date.now(),
          error: true,
        },
      ]);
    } finally {
      setSmartDeskLoading(false);
    }
  };

  const handleNewChat = () => {
    setSmartDeskMessages([]);
    setConversationHistory([]);
    setSmartDeskInput("");
  };

  const handleSaveEdit = async () => {
    if (!editingMessage || editingMessage.type !== "user") return;
    const editedContent = editMessageContent.trim();
    if (!editedContent) {
      setEditDialogOpen(false);
      setEditMessageContent("");
      setEditingMessage(null);
      return;
    }
    if (editedContent.split(/\s+/).length > getChatWordLimit()) {
      toast.warn(`Edited message exceeds ${getChatWordLimit()} word limit`);
      return;
    }
    const editIndex = conversation.findIndex(
      (msg) => msg.id === editingMessage.id,
    );
    if (editIndex === -1) return;
    const updatedUserMessage = {
      ...editingMessage,
      content: editedContent,
      timestamp: new Date(),
    };
    const newConversation = [
      ...conversation.slice(0, editIndex),
      updatedUserMessage,
    ];
    setConversation(newConversation);
    setIsLoading(true);
    setEditDialogOpen(false);
    setEditMessageContent("");
    let chatId = currentChatId;
    if (chatId) updateChatHistory(chatId, newConversation);
    try {
      const historyForAPI = conversation.slice(0, editIndex).map((msg) => ({
        role: msg.type === "user" ? "user" : "assistant",
        content: msg.content,
      }));
      const response = await aiSmartDesk(editedContent, "", historyForAPI);
      const aiResponseText =
        response.data?.data?.response ||
        response.data?.response ||
        (typeof response.data === "string" ? response.data : null) ||
        response?.response ||
        "Received response but couldn't parse it.";
      const aiMessage = {
        id: generateId(),
        type: "ai",
        content: aiResponseText,
        timestamp: new Date(),
      };
      const finalConversation = [...newConversation, aiMessage];
      setConversation(finalConversation);
      if (chatId) updateChatHistory(chatId, finalConversation);
      showToast("Message edited and regenerated!", "success");
    } catch (error) {
      const errorMessage = {
        id: Date.now(),
        type: "ai",
        content:
          "Sorry, something went wrong. Error: " +
          (error.response?.data?.message || error.message),
        timestamp: new Date(),
        isError: true,
      };
      setConversation([...newConversation, errorMessage]);
      if (chatId) updateChatHistory(chatId, [...newConversation, errorMessage]);
    } finally {
      setIsLoading(false);
      setEditingMessage(null);
    }
  };

  const handleChatMenuOpen = (event, chat) => {
    setChatMenuAnchor(event.currentTarget);
    setSelectedChat(chat);
  };
  const handleChatMenuClose = () => {
    setChatMenuAnchor(null);
    setSelectedChat(null);
  };
  const handleRenameChat = () => {
    if (selectedChat) {
      setNewChatTitle(selectedChat.title);
      setRenameDialogOpen(true);
      handleChatMenuClose();
    }
  };
  const handleSaveRename = () => {
    if (!selectedChat || !newChatTitle.trim()) {
      toast.warning("Please enter a valid title");
      return;
    }
    setChatHistory(
      chatHistory.map((chat) =>
        chat.id === selectedChat.id
          ? { ...chat, title: newChatTitle.trim(), timestamp: new Date() }
          : chat,
      ),
    );
    setRenameDialogOpen(false);
    setNewChatTitle("");
    setSelectedChat(null);
    showToast("Chat renamed successfully!", "success");
  };
  const handleDeleteChat = () => {
    if (selectedChat) {
      setChatHistory(chatHistory.filter((chat) => chat.id !== selectedChat.id));
      if (currentChatId === selectedChat.id) {
        setCurrentChatId(null);
        setConversation([]);
      }
      handleChatMenuClose();
      showToast("Chat deleted successfully!", "success");
    }
  };
  const handlePinChat = () => {
    if (selectedChat) {
      setChatHistory(
        chatHistory.map((chat) =>
          chat.id === selectedChat.id
            ? { ...chat, pinned: !chat.pinned }
            : chat,
        ),
      );
      handleChatMenuClose();
      showToast(
        selectedChat.pinned ? "Chat unpinned" : "Chat pinned",
        "success",
      );
    }
  };
  const handleShareChat = () => {
    if (selectedChat) {
      navigator.clipboard.writeText(
        JSON.stringify(
          {
            title: selectedChat.title,
            messages: selectedChat.messages,
            timestamp: selectedChat.timestamp,
          },
          null,
          2,
        ),
      );
      handleChatMenuClose();
      showToast("Chat data copied to clipboard!", "success");
    }
  };
  const loadChat = (chat) => {
    setConversation(chat.messages || []);
    setCurrentChatId(chat.id);
  };
  const startNewChat = () => {
    setConversation([]);
    setCurrentChatId(null);
    showToast("New chat started!", "success");
  };

  const getChatPreview = (chat) => {
    if (!chat.messages || chat.messages.length === 0) return "Empty chat";
    const userMessages = chat.messages.filter((msg) => msg.type === "user");
    if (userMessages.length > 0) {
      const p = userMessages[userMessages.length - 1].content
        .split(" ")
        .slice(0, 8)
        .join(" ");
      return (
        p +
        (userMessages[userMessages.length - 1].content.split(" ").length > 8
          ? "..."
          : "")
      );
    }
    const aiMessages = chat.messages.filter((msg) => msg.type === "ai");
    if (aiMessages.length > 0) {
      const p = aiMessages[aiMessages.length - 1].content
        .split(" ")
        .slice(0, 8)
        .join(" ");
      return (
        "AI: " +
        p +
        (aiMessages[aiMessages.length - 1].content.split(" ").length > 8
          ? "..."
          : "")
      );
    }
    return "Empty chat";
  };

  const getSortedChatHistory = () =>
    [...chatHistory].sort((a, b) => {
      if (a.pinned && !b.pinned) return -1;
      if (!a.pinned && b.pinned) return 1;
      return new Date(b.timestamp) - new Date(a.timestamp);
    });

  // ── Tab config ──
  const tabs = [
    {
      id: "generator",
      label: "AI GenArt Studio",
      icon: <ImageIcon sx={{ fontSize: 18 }} />,
      color: "#8B5CF6",
      gradient: "linear-gradient(135deg,#8B5CF6,#EC4899)",
      access: true,
      premium: false,
    },
    {
      id: "chatbot",
      label: "AI SmartDesk",
      icon: <ChatIcon sx={{ fontSize: 18 }} />,
      color: "#A855F7",
      gradient: "linear-gradient(135deg,#A855F7,#C084FC)",
      access: accessToAISmartDesk,
      premium: !accessToAISmartDesk,
    },
    {
      id: "humanizer",
      label: "Humanize ToneCraft",
      icon: <PersonIcon sx={{ fontSize: 18 }} />,
      color: "#10B981",
      gradient: "linear-gradient(135deg,#10B981,#34D399)",
      access: accessToHumanizer,
      premium: !accessToHumanizer,
    },
  ];

  // ── Chat History Sidebar ──
  const renderChatHistory = () => (
    <Card sx={{ height: 540, display: "flex", flexDirection: "column" }}>
      <Box
        sx={{
          p: 2.5,
          borderBottom: "1px solid rgba(15,23,42,0.07)",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          flexShrink: 0,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <History sx={{ fontSize: 18, color: "#323232" }} />
          <Typography variant="h6" sx={{ fontSize: "0.95rem" }}>
            Chat History
          </Typography>
        </Box>
        <Button
          size="small"
          startIcon={<AddComment sx={{ fontSize: 15 }} />}
          onClick={startNewChat}
          sx={{
            color: "#A855F7",
            border: "1.5px solid rgba(168,85,247,0.3)",
            borderRadius: 2,
            px: 1.5,
            py: 0.5,
            fontSize: "0.75rem",
            fontWeight: 700,
            "&:hover": {
              background: "rgba(168,85,247,0.08)",
              borderColor: "#A855F7",
            },
          }}
        >
          New
        </Button>
      </Box>
      <Box sx={{ flex: 1, overflow: "auto", p: 1.5 }}>
        {chatHistory.length === 0 ? (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              height: "100%",
              py: 4,
              textAlign: "center",
            }}
          >
            <Box sx={{ fontSize: "2rem", mb: 1.5, opacity: 0.4 }}>💬</Box>
            <Typography
              variant="body2"
              sx={{ color: "#4d4d4d", fontWeight: 500 }}
            >
              No chats yet
            </Typography>
            <Typography variant="caption" sx={{ color: "#4a4a4a", mt: 0.5 }}>
              Start a conversation!
            </Typography>
          </Box>
        ) : (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 0.75 }}>
            {getSortedChatHistory().map((chat) => (
              <Box
                key={chat.id}
                onClick={() => loadChat(chat)}
                sx={{
                  p: 1.5,
                  borderRadius: 2,
                  cursor: "pointer",
                  border: `1.5px solid ${currentChatId === chat.id ? "rgba(168,85,247,0.4)" : "rgba(15,23,42,0.06)"}`,
                  background:
                    currentChatId === chat.id
                      ? "rgba(227, 215, 238, 0.25)"
                      : "transparent",
                  transition: "all 0.15s",
                  "&:hover": {
                    background: "rgba(168,85,247,0.04)",
                    borderColor: "rgba(168,85,247,0.2)",
                  },
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "flex-start",
                    justifyContent: "space-between",
                    gap: 1,
                  }}
                >
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 0.5,
                        mb: 0.25,
                      }}
                    >
                      {chat.pinned && (
                        <PushPin sx={{ fontSize: 12, color: "#A855F7" }} />
                      )}
                      <Typography
                        variant="body2"
                        sx={{
                          fontWeight: 700,
                          color: "#0F172A",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                          fontSize: "0.8rem",
                        }}
                      >
                        {chat.title}
                      </Typography>
                    </Box>
                    <Typography
                      variant="caption"
                      sx={{
                        color: "#565656",
                        display: "block",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                        lineHeight: 1.4,
                      }}
                    >
                      {getChatPreview(chat)}
                    </Typography>
                    <Typography
                      variant="caption"
                      sx={{ color: "#414141", fontSize: "0.65rem" }}
                    >
                      {new Date(chat.timestamp).toLocaleDateString()} ·{" "}
                      {chat.messages?.length || 0} msgs
                    </Typography>
                  </Box>
                  <IconButton
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleChatMenuOpen(e, chat);
                    }}
                    sx={{
                      color: "#4d4d4d",
                      width: 24,
                      height: 24,
                      flexShrink: 0,
                      "&:hover": {
                        color: "#949494",
                        background: "rgba(15,23,42,0.04)",
                      },
                    }}
                  >
                    <MoreVert sx={{ fontSize: 15 }} />
                  </IconButton>
                </Box>
              </Box>
            ))}
          </Box>
        )}
      </Box>
    </Card>
  );

  // ── Chatbot ──
  const renderChatbot = () => {
    if (!accessToAISmartDesk) {
      return (
        <Grid container spacing={3}>
          <Grid xs={12}>
            <Card>
              <CardContent sx={{ py: 6, textAlign: "center" }}>
                <Box
                  sx={{
                    width: 72,
                    height: 72,
                    borderRadius: 3,
                    background: "rgba(168,85,247,0.08)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    mx: "auto",
                    mb: 2,
                  }}
                >
                  <ChatIcon
                    sx={{ fontSize: 36, color: "rgba(168,85,247,0.4)" }}
                  />
                </Box>
                <Typography variant="h6" sx={{ color: "#0F172A", mb: 1 }}>
                  AI SmartDesk Not Available
                </Typography>
                <Typography variant="body2" sx={{ color: "#94A3B8" }}>
                  This feature is not included in your current plan. Upgrade to
                  access AI chat.
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      );
    }

    const chatWordCount = chatMessage.trim()
      ? chatMessage.trim().split(/\s+/).length
      : 0;

    return (
      <Grid container spacing={2.5}>
        <Grid xs={12} lg={3} md={4}>
          {renderChatHistory()}
        </Grid>
        <Grid xs={12} lg={9} md={8}>
          <Card
            sx={{
              height: 540,
              display: "flex",
              flexDirection: "column",
              overflow: "hidden",
            }}
          >
            {/* Chat Header */}
            <Box
              sx={{
                px: 2.5,
                py: 1.75,
                borderBottom: "1px solid rgba(15,23,42,0.07)",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                flexShrink: 0,
                background: "rgba(248,250,252,0.7)",
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                <Box
                  sx={{
                    width: 36,
                    height: 36,
                    borderRadius: 2,
                    background: "linear-gradient(135deg,#A855F7,#C084FC)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    boxShadow: "0 2px 8px rgba(168,85,247,0.3)",
                  }}
                >
                  <Psychology sx={{ color: "white", fontSize: 20 }} />
                </Box>
                <Box>
                  <Typography
                    variant="subtitle1"
                    sx={{ lineHeight: 1.2, fontSize: "0.9rem" }}
                  >
                    {currentChatId
                      ? chatHistory.find((c) => c.id === currentChatId)
                          ?.title || "AI SmartDesk"
                      : "AI SmartDesk"}
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{
                      color: "#10B981",
                      fontWeight: 600,
                      display: "flex",
                      alignItems: "center",
                      gap: 0.5,
                    }}
                  >
                    <Box
                      sx={{
                        width: 6,
                        height: 6,
                        borderRadius: "50%",
                        background: "#10B981",
                        display: "inline-block",
                      }}
                    />{" "}
                    Online
                  </Typography>
                </Box>
              </Box>
              <Chip
                label={`${getChatWordLimit()} word limit`}
                size="small"
                sx={{
                  background: "rgba(168,85,247,0.08)",
                  color: "#A855F7",
                  border: "1px solid rgba(168,85,247,0.2)",
                  fontWeight: 700,
                  fontSize: "0.7rem",
                }}
              />
            </Box>

            {/* Messages */}
            <Box
              ref={messagesContainerRef}
              sx={{
                flex: 1,
                overflow: "auto",
                pt: 2.5,
                px: 2.5,
                pb: 0,
                display: "flex",
                flexDirection: "column",
                gap: 2,
                background: "rgba(248,250,252,0.4)",
                minHeight: 0,
              }}
            >
              {conversation.length === 0 && !isLoading && (
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    height: "100%",
                    gap: 3,
                    py: 4,
                  }}
                >
                  <Box
                    sx={{
                      width: 72,
                      height: 72,
                      borderRadius: 4,
                      background:
                        "linear-gradient(135deg,rgba(168,85,247,0.1),rgba(79,110,247,0.1))",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      border: "1.5px solid rgba(168,85,247,0.15)",
                    }}
                  >
                    <AutoAwesome sx={{ fontSize: 36, color: "#A855F7" }} />
                  </Box>
                  <Box sx={{ textAlign: "center" }}>
                    <Typography variant="h6" sx={{ mb: 0.5, fontWeight: 700 }}>
                      Hello! I'm your AI assistant
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{ color: "#5c5c5c", maxWidth: 340 }}
                    >
                      How can I help you today? Ask me anything or try one of
                      the quick prompts below.
                    </Typography>
                  </Box>
                </Box>
              )}

              {conversation.map((message) => (
                <Box
                  key={message.id}
                  sx={{
                    display: "flex",
                    justifyContent:
                      message.type === "user" ? "flex-end" : "flex-start",
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "flex-end",
                      gap: 1,
                      maxWidth: "72%",
                      flexDirection:
                        message.type === "user" ? "row-reverse" : "row",
                    }}
                  >
                    <Avatar
                      sx={{
                        width: 30,
                        height: 30,
                        bgcolor:
                          message.type === "user" ? "#4F6EF7" : "#A855F7",
                        fontSize: "0.7rem",
                        fontWeight: 800,
                        flexShrink: 0,
                      }}
                    >
                      {message.type === "user" ? "U" : "AI"}
                    </Avatar>
                    <Box>
                      <Paper
                        elevation={0}
                        sx={{
                          px: 2,
                          py: 1.5,
                          borderRadius:
                            message.type === "user" ? "3 3 0.5 3" : "0.5 3 3 3",
                          background:
                            message.type === "user"
                              ? "linear-gradient(135deg,#4F6EF7,#2D4FD6)"
                              : "white",
                          color: message.type === "user" ? "white" : "#0F172A",
                          boxShadow:
                            message.type === "user"
                              ? "0 4px 12px rgba(79,110,247,0.25)"
                              : "0 2px 8px rgba(15,23,42,0.07)",
                          border: message.isError
                            ? "1.5px solid rgba(239,68,68,0.3)"
                            : "none",
                          position: "relative",
                        }}
                      >
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "flex-start",
                            gap: 0.5,
                          }}
                        >
                          <Typography
                            variant="body2"
                            sx={{
                              whiteSpace: "pre-wrap",
                              lineHeight: 1.7,
                              color:
                                message.type === "user" ? "white" : "#0F172A",
                            }}
                          >
                            {message.content}
                          </Typography>
                          <IconButton
                            size="small"
                            onClick={(e) => handleMessageMenuOpen(e, message)}
                            sx={{
                              color:
                                message.type === "user"
                                  ? "rgba(255,255,255,0.5)"
                                  : "#CBD5E1",
                              width: 24,
                              height: 24,
                              flexShrink: 0,
                              mt: -0.5,
                              "&:hover": {
                                color:
                                  message.type === "user" ? "white" : "#64748B",
                                background: "rgba(0,0,0,0.08)",
                              },
                            }}
                          >
                            <MoreVert sx={{ fontSize: 14 }} />
                          </IconButton>
                        </Box>
                        <Typography
                          variant="caption"
                          sx={{
                            display: "block",
                            mt: 0.5,
                            opacity: 0.6,
                            color:
                              message.type === "user"
                                ? "rgba(255,255,255,0.8)"
                                : "#94A3B8",
                            fontSize: "0.65rem",
                          }}
                        >
                          {message.timestamp.toLocaleTimeString([], {
                            hour: "2-digit",
                            minute: "2-digit",
                          })}
                        </Typography>
                      </Paper>
                    </Box>
                  </Box>
                </Box>
              ))}

              {isLoading && (
                <Box sx={{ display: "flex", alignItems: "flex-end", gap: 1 }}>
                  <Avatar
                    sx={{
                      width: 30,
                      height: 30,
                      bgcolor: "#A855F7",
                      fontSize: "0.7rem",
                      fontWeight: 800,
                    }}
                  >
                    AI
                  </Avatar>
                  <Paper
                    elevation={0}
                    sx={{
                      px: 2,
                      py: 1.5,
                      borderRadius: "0.5 3 3 3",
                      background: "white",
                      boxShadow: "0 2px 8px rgba(15,23,42,0.07)",
                    }}
                  >
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      {[0, 1, 2].map((i) => (
                        <Box
                          key={i}
                          sx={{
                            width: 7,
                            height: 7,
                            borderRadius: "50%",
                            background: "#A855F7",
                            animation: "bounce 1.2s ease-in-out infinite",
                            animationDelay: `${i * 0.18}s`,
                            "@keyframes bounce": {
                              "0%,60%,100%": { transform: "translateY(0)" },
                              "30%": { transform: "translateY(-6px)" },
                            },
                          }}
                        />
                      ))}
                    </Box>
                  </Paper>
                </Box>
              )}
              <div ref={messagesEndRef} />
            </Box>

            {/* Quick Prompts */}
            {conversation.length === 0 && !isLoading && (
              <Box
                sx={{
                  px: 2.5,
                  py: 1.5,
                  borderTop: "1px solid rgba(15,23,42,0.06)",
                  background: "rgba(248,250,252,0.7)",
                }}
              >
                <Typography
                  variant="caption"
                  sx={{
                    color: "#4f4f4f",
                    fontWeight: 600,
                    mb: 1,
                    display: "block",
                  }}
                >
                  QUICK PROMPTS
                </Typography>
                <Box sx={{ display: "flex", gap: 0.75, flexWrap: "wrap" }}>
                  {[
                    "Write a professional email",
                    "Create a marketing strategy",
                    "Debug this code",
                    "Summarize this article",
                    "Generate creative ideas",
                  ].map((prompt, idx) => (
                    <Chip
                      key={idx}
                      label={prompt}
                      size="small"
                      onClick={() => handleSendMessage(prompt, true)}
                      sx={{
                        cursor: "pointer",
                        background: "rgba(168,85,247,0.06)",
                        color: "#A855F7",
                        border: "1px solid rgba(168,85,247,0.2)",
                        fontWeight: 600,
                        fontSize: "0.7rem",
                        "&:hover": {
                          background: "rgba(168,85,247,0.12)",
                          borderColor: "#A855F7",
                        },
                        transition: "all 0.15s",
                      }}
                    />
                  ))}
                </Box>
              </Box>
            )}

            {/* Input */}
            <Box
              sx={{
                px: 2.5,
                py: 2,
                borderTop: "1px solid rgba(15,23,42,0.07)",
                background: "rgba(255,255,255,0.8)",
                backdropFilter: "blur(8px)",
                flexShrink: 0,
              }}
            >
              <Box sx={{ display: "flex", gap: 1.5, alignItems: "flex-end" }}>
                <Box sx={{ flex: 1 }}>
                  <TextField
                    fullWidth
                    placeholder="Type your message here..."
                    value={chatMessage}
                    onChange={(e) => setChatMessage(e.target.value)}
                    size="small"
                    multiline
                    maxRows={3}
                    disabled={isLoading || !accessToAISmartDesk}
                    onKeyPress={(e) => {
                      if (e.key === "Enter" && !e.shiftKey) {
                        e.preventDefault();
                        handleSendMessage();
                      }
                    }}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        background: "rgb(254, 253, 253)",
                      },
                    }}
                    helperText={
                      <Box
                        component="span"
                        sx={{
                          color:
                            chatWordCount > getChatWordLimit()
                              ? "#EF4444"
                              : "#b23eff",
                          fontSize: "0.68rem",
                        }}
                      >
                        {chatWordCount} / {getChatWordLimit()} words
                      </Box>
                    }
                  />
                </Box>
                <IconButton
                  onClick={() => handleSendMessage()}
                  disabled={
                    isLoading || !chatMessage.trim() || !accessToAISmartDesk
                  }
                  sx={{
                    width: 44,
                    height: 44,
                    borderRadius: 2,
                    flexShrink: 0,
                    mb: "20px",
                    background:
                      isLoading || !chatMessage.trim()
                        ? "rgba(15,23,42,0.07)"
                        : "linear-gradient(135deg,#A855F7,#7E22CE)",
                    color:
                      isLoading || !chatMessage.trim() ? "#CBD5E1" : "white",
                    boxShadow:
                      isLoading || !chatMessage.trim()
                        ? "none"
                        : "0 4px 12px rgba(168,85,247,0.35)",
                    transition: "all 0.2s",
                    "&:hover:not(:disabled)": {
                      transform: "translateY(-2px)",
                      boxShadow: "0 6px 16px rgba(168,85,247,0.45)",
                    },
                  }}
                >
                  <Send sx={{ fontSize: 20 }} />
                </IconButton>
              </Box>
            </Box>
          </Card>
        </Grid>
      </Grid>
    );
  };

  return (
    <ThemeProvider theme={aiIntelligenzLightTheme}>
      <AIIntelligenceBackground>
        <SubscriptionRequired open={subReqOpen} setOpen={setSubReqOpen} />

        <Box
          sx={{
            height: "100%",
            px: { xs: 2, sm: 3 },
            pt: { xs: 2, sm: 3 },
            pb: 0,
            position: "relative",
            zIndex: 5,
            display: "flex",
            flexDirection: "column",
            boxSizing: "border-box",
          }}
        >
          <ScreenInfoOverlay activeTab={activeTab} />

          <Box
            sx={{
              position: "sticky",
              top: 0,
              zIndex: 12,
              pb: 2.5,
              mb: 3.5,
              background:
                "linear-gradient(180deg, rgba(238, 242, 255, 0.98) 0%, rgba(245, 243, 255, 0.95) 72%, rgba(239, 246, 255, 0.88) 100%)",
              backdropFilter: "blur(14px)",
            }}
          >
            {/* Header */}
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                mb: 4,
                pt: 2,
              }}
            >
              <Box
                sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}
              >
                <Box
                  sx={{
                    width: 6,
                    height: 6,
                    borderRadius: "50%",
                    background: "#4F6EF7",
                  }}
                />
                <Typography
                  sx={{
                    fontSize: "1rem",
                    fontWeight: 800,
                    letterSpacing: "0.18em",
                    color: "#4F6EF7",
                    textTransform: "uppercase",
                  }}
                >
                  AI Smart Engine Hub
                </Typography>
                <Box
                  sx={{
                    width: 6,
                    height: 6,
                    borderRadius: "50%",
                    background: "#A855F7",
                  }}
                />
              </Box>
            </Box>

            {/* Tab Navigation */}
            <Box
              sx={{
                display: "flex",
                gap: 1.5,
                p: 1.5,
                background: "rgba(255,255,255,0.7)",
                backdropFilter: "blur(12px)",
                borderRadius: 3,
                border: "1px solid rgba(15,23,42,0.07)",
                boxShadow: "0 4px 16px rgba(15,23,42,0.05)",
                flexWrap: "wrap",
              }}
            >
              {tabs.map((tab) => {
                const isActive = activeTab === tab.id;
                return (
                  <Button
                    key={tab.id}
                    fullWidth={false}
                    variant="text"
                    startIcon={
                      <Box
                        sx={{
                          width: 28,
                          height: 28,
                          borderRadius: 1.5,
                          background: isActive
                            ? tab.gradient
                            : "rgba(15,23,42,0.06)",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          transition: "all 0.2s",
                          "& svg": {
                            color: isActive ? "white" : tab.color,
                            transition: "color 0.2s",
                          },
                        }}
                      >
                        {tab.icon}
                      </Box>
                    }
                    onClick={() => {
                      if (tab.id === "generator") {
                        if (!accessToAIGenArtStudio) {
                          toast.warn(
                            "AI GenArt Studio is not available in your plan",
                          );
                          return;
                        }
                        setActiveTab(tab.id);
                        return;
                      }
                      if (!tab.access) {
                        toast.warn(
                          `${tab.label} is not available in your plan`,
                        );
                        return;
                      }
                      setActiveTab(tab.id);
                    }}
                    sx={{
                      flex: "1 1 auto",
                      minWidth: 0,
                      py: 1.2,
                      px: 2,
                      borderRadius: 2.5,
                      background: isActive ? `${tab.color}10` : "transparent",
                      border: `1.5px solid ${isActive ? `${tab.color}40` : "transparent"}`,
                      color: isActive ? tab.color : "#64748B",
                      fontWeight: isActive ? 800 : 600,
                      fontSize: "0.82rem",
                      transition: "all 0.2s",
                      "&:hover": {
                        background: `${tab.color}08`,
                        borderColor: `${tab.color}30`,
                        color: tab.color,
                      },
                      boxShadow: isActive
                        ? `0 2px 12px ${tab.color}20`
                        : "none",
                    }}
                  >
                    {tab.label}
                    {tab.premium && (
                      <Chip
                        label="Pro"
                        size="small"
                        sx={{
                          ml: 0.75,
                          height: 16,
                          fontSize: "0.6rem",
                          background: tab.color,
                          color: "white",
                          fontWeight: 800,
                        }}
                      />
                    )}
                  </Button>
                );
              })}
            </Box>
          </Box>

          {/* Tab Content */}
          <Box
            sx={{
              flex: 1,
              minHeight: 0,
              animation: "fadeIn 0.3s ease",
              "@keyframes fadeIn": {
                "0%": { opacity: 0, transform: "translateY(8px)" },
                "100%": { opacity: 1, transform: "translateY(0)" },
              },
            }}
          >
            {activeTab === "generator" && (
              <AIGenArtStudio
                accessToAIGenArtStudio={accessToAIGenArtStudio}
                accessToArtStyle={accessToArtStyle}
                userID={userID}
              />
            )}
            {activeTab === "chatbot" && renderChatbot()}
            {activeTab === "humanizer" && (
              <HumanizerTab
                humanizerLevel={humanizerLevel}
                setHumanizerLevel={setHumanizerLevel}
                userID={userID}
                accessToLight={accessToLightLevel}
                accessToBalanced={accessToBalancedLevel}
                accessToMaximum={accessToMaximumLevel}
                userPlan={userPlan}
              />
            )}
          </Box>
        </Box>

        {/* Menus & Dialogs */}
        <Menu
          anchorEl={messageMenuAnchor}
          open={Boolean(messageMenuAnchor)}
          onClose={handleMessageMenuClose}
          PaperProps={{
            sx: {
              borderRadius: 2,
              boxShadow: "0 8px 32px rgba(15,23,42,0.12)",
              border: "1px solid rgba(15,23,42,0.07)",
              mt: 0.5,
            },
          }}
        >
          <MenuItem
            onClick={handleCopyMessage}
            sx={{
              gap: 1.5,
              borderRadius: 1,
              mx: 0.5,
              fontSize: "0.85rem",
              fontWeight: 600,
            }}
          >
            <ContentCopy sx={{ fontSize: 17, color: "#0f6df0" }} /> Copy
          </MenuItem>
          {selectedMessage?.type === "user" && (
            <MenuItem
              onClick={handleEditMessage}
              sx={{
                gap: 1.5,
                borderRadius: 1,
                mx: 0.5,
                fontSize: "0.85rem",
                fontWeight: 600,
              }}
            >
              <Edit sx={{ fontSize: 17, color: "#d03636" }} /> Edit
            </MenuItem>
          )}
        </Menu>

        <Menu
          anchorEl={chatMenuAnchor}
          open={Boolean(chatMenuAnchor)}
          onClose={handleChatMenuClose}
          PaperProps={{
            sx: {
              borderRadius: 2,
              boxShadow: "0 8px 32px rgba(15,23,42,0.12)",
              border: "1px solid rgba(15,23,42,0.07)",
              mt: 0.5,
              minWidth: 160,
            },
          }}
        >
          <MenuItem
            onClick={handleRenameChat}
            sx={{
              gap: 1.5,
              borderRadius: 1,
              mx: 0.5,
              fontSize: "0.85rem",
              fontWeight: 600,
              color: "#6e2df0",
            }}
          >
            <Edit sx={{ fontSize: 17, color: "#6e2df0" }} />
            Rename
          </MenuItem>
          <MenuItem
            onClick={handlePinChat}
            sx={{
              gap: 1.5,
              borderRadius: 1,
              mx: 0.5,
              fontSize: "0.85rem",
              fontWeight: 600,
              color: "#ea6138",
            }}
          >
            <PushPin sx={{ fontSize: 17, color: "#ea6138" }} />
            {selectedChat?.pinned ? "Unpin" : "Pin"}
          </MenuItem>
          <MenuItem
            onClick={handleShareChat}
            sx={{
              gap: 1.5,
              borderRadius: 1,
              mx: 0.5,
              fontSize: "0.85rem",
              fontWeight: 600,
              color: "#277ef8",
            }}
          >
            <Share sx={{ fontSize: 17, color: "#277ef8" }} />
            Share
          </MenuItem>
          <Divider sx={{ my: 0.5 }} />
          <MenuItem
            onClick={handleDeleteChat}
            sx={{
              gap: 1.5,
              borderRadius: 1,
              mx: 0.5,
              fontSize: "0.85rem",
              fontWeight: 600,
              color: "#EF4444",
            }}
          >
            <Delete sx={{ fontSize: 17, color: "#EF4444" }} />
            Delete
          </MenuItem>
        </Menu>

        {/* Edit Dialog */}
        <Dialog
          open={editDialogOpen}
          onClose={() => {
            setEditDialogOpen(false);
            setEditMessageContent("");
            setEditingMessage(null);
          }}
          maxWidth="sm"
          fullWidth
          PaperProps={{
            sx: {
              borderRadius: 3,
              boxShadow: "0 24px 64px rgba(15,23,42,0.14)",
            },
          }}
        >
          <DialogTitle
            sx={{ pb: 1, borderBottom: "1px solid rgba(15,23,42,0.07)" }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
              <Edit sx={{ color: "#4F6EF7", fontSize: 20 }} />
              <Typography
                variant="h6"
                sx={{ color: "linear-gradient(135deg,#4F6EF7,#A855F7)" }}
              >
                Edit Message
              </Typography>
            </Box>
          </DialogTitle>
          <DialogContent sx={{ pt: 2 }}>
            <TextField
              autoFocus
              fullWidth
              multiline
              rows={4}
              value={editMessageContent}
              onChange={(e) => setEditMessageContent(e.target.value)}
              variant="outlined"
              sx={{ mt: 1 }}
              FormHelperTextProps={{
                sx: { color: "#4F6EF7", fontWeight: 600 },
              }}
              helperText={`${editMessageContent.trim().split(/\s+/).length} / ${getChatWordLimit()} words`}
            />
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 3, gap: 1 }}>
            <Button
              onClick={() => {
                setEditDialogOpen(false);
                setEditMessageContent("");
                setEditingMessage(null);
              }}
              variant="outlined"
              sx={{
                borderRadius: 2,
                color: "#2779ea",
                borderColor: "rgba(15,23,42,0.15)",
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleSaveEdit}
              variant="contained"
              sx={{
                borderRadius: 2,
                background: "linear-gradient(135deg,#4F6EF7,#A855F7)",
                fontWeight: 700,
              }}
            >
              Save & Regenerate
            </Button>
          </DialogActions>
        </Dialog>

        {/* Rename Dialog */}
        <Dialog
          open={renameDialogOpen}
          onClose={() => setRenameDialogOpen(false)}
          maxWidth="sm"
          fullWidth
          PaperProps={{
            sx: {
              borderRadius: 3,
              boxShadow: "0 24px 64px rgba(15,23,42,0.14)",
            },
          }}
        >
          <DialogTitle
            sx={{ pb: 1, borderBottom: "1px solid rgba(15,23,42,0.07)" }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
              <Edit sx={{ color: "#A855F7", fontSize: 20 }} />
              <Typography variant="h6">Rename Chat</Typography>
            </Box>
          </DialogTitle>
          <DialogContent sx={{ pt: 2 }}>
            <TextField
              autoFocus
              fullWidth
              value={newChatTitle}
              onChange={(e) => setNewChatTitle(e.target.value)}
              variant="outlined"
              sx={{ mt: 1 }}
              placeholder="Enter new chat title..."
            />
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 3, gap: 1 }}>
            <Button
              onClick={() => setRenameDialogOpen(false)}
              variant="outlined"
              sx={{
                borderRadius: 2,
                color: "#1171f8",
                borderColor: "rgba(15,23,42,0.15)",
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleSaveRename}
              variant="contained"
              disabled={!newChatTitle.trim()}
              sx={{
                borderRadius: 2,
                background: "linear-gradient(135deg,#A855F7,#7E22CE)",
                fontWeight: 700,
              }}
            >
              Save
            </Button>
          </DialogActions>
        </Dialog>

        {/* Loading Backdrop */}
        <Backdrop
          open={isLoading}
          sx={{ color: "#fff", zIndex: 9999, backdropFilter: "blur(4px)" }}
        >
          <Box
            sx={{
              textAlign: "center",
              p: 4,
              borderRadius: 4,
              background: "rgba(15,23,42,0.85)",
              border: "1px solid rgba(79,110,247,0.3)",
            }}
          >
            <Box sx={{ position: "relative", display: "inline-flex", mb: 2 }}>
              <CircularProgress size={56} sx={{ color: "#4F6EF7" }} />
              <Box
                sx={{
                  position: "absolute",
                  inset: 0,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <AutoAwesome sx={{ color: "#A855F7", fontSize: 24 }} />
              </Box>
            </Box>
            <Typography variant="h6" sx={{ fontWeight: 700, mb: 0.5 }}>
              Processing your request...
            </Typography>
            <Typography variant="body2" sx={{ opacity: 0.6 }}>
              AI is working its magic
            </Typography>
          </Box>
        </Backdrop>

        {/* Success Snackbar */}
        <Snackbar
          open={showSuccess}
          autoHideDuration={3000}
          onClose={() => setShowSuccess(false)}
          anchorOrigin={{ vertical: "top", horizontal: "right" }}
        >
          <Alert
            severity="success"
            sx={{
              borderRadius: 2.5,
              boxShadow: "0 8px 24px rgba(16,185,129,0.2)",
              border: "1px solid rgba(16,185,129,0.2)",
              fontWeight: 600,
            }}
          >
            {successMessage || "Action completed successfully!"}
          </Alert>
        </Snackbar>

        <ToastContainer toastStyle={{ borderRadius: "12px" }} />
      </AIIntelligenceBackground>
    </ThemeProvider>
  );
}
